import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  FileCheck, Download, RefreshCw, CheckCircle, XCircle, 
  AlertCircle, User, Calendar, BookOpen, Award, ClipboardCheck,
  Eye, FileText, Upload, MessageSquare, Briefcase, Shield, Mail, Phone, Loader2, Database, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Wrench, Archive } from "lucide-react";
import { FinanzierungRueckfall } from "@/components/teilnehmer/FinanzierungRueckfall";
import { DublettenZusammenfuehren } from "@/components/audit/DublettenZusammenfuehren";
import TeilnehmerDetail from "@/components/TeilnehmerDetail";
import QMDokumente from "@/components/QMDokumente";
import AnhangRepair from "@/components/AnhangRepair";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Kurs-Informationen für AZAV
const KURS_INFO = {
  "LG1 & LG2": { ue: 208, praktikum: false },
  "Ambulanter Pflegeassistent": { ue: 300, praktikum: false },
  "Pflegehelfer/Betreuungskraft": { ue: 429, praktikum: true, praktikum_wochen: 1 },
};

const AZAVMappe = ({ modus = "mappe" }) => {
  const { user } = useAuth();
  const isReadOnly = user?.role === "dozent";
  const [data, setData] = useState({ total: 0, teilnehmer: [], statistik: {} });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [jahrFilter, setJahrFilter] = useState("all"); // "all" | "2025" | "2026" | "2027"
  
  // TeilnehmerDetail Dialog
  const [selectedTeilnehmerId, setSelectedTeilnehmerId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // === AZAV-Tools: Self-Audit + Bulk-Resend State ===
  const [auditOpen, setAuditOpen] = useState(false);
  const [qmDokumenteOpen, setQmDokumenteOpen] = useState(false);
  const [anhangRepairOpen, setAnhangRepairOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditReport, setAuditReport] = useState(null);
  const [auditFilter, setAuditFilter] = useState({
    kurs: "", bg_sz: "", bestanden: "", abgebrochen: "", jahr: "2026"
  });
  const [availableKurse, setAvailableKurse] = useState([]);

  // Verfügbare Kurs-Namen laden (für Dropdown)
  useEffect(() => {
    axios.get(`${API}/kurse/verfuegbare-namen`)
      .then(r => setAvailableKurse(r.data || []))
      .catch(() => {});
  }, []);

  const [resendOpen, setResendOpen] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  const [resendDryRun, setResendDryRun] = useState(true);
  const [resendMethode, setResendMethode] = useState("whatsapp");

  // === Sammelversand „Fehlende Dokumente" ===
  const [missingDocsOpen, setMissingDocsOpen] = useState(false);
  const [missingDocsLoading, setMissingDocsLoading] = useState(false);
  const [missingDocsPreview, setMissingDocsPreview] = useState(null);  // dry_run result
  const [missingDocsResult, setMissingDocsResult] = useState(null);    // live result
  const [missingDocsMethode, setMissingDocsMethode] = useState("whatsapp");
  const [missingDocsMinDate, setMissingDocsMinDate] = useState("2025-01-01");
  const [missingDocsTypes, setMissingDocsTypes] = useState({
    massnahmevertrag: true,
    datenschutz: true,
    erstgespraech: true,
  });

  const runMissingDocsPreview = async () => {
    setMissingDocsLoading(true);
    setMissingDocsResult(null);
    try {
      const dokumente = Object.entries(missingDocsTypes)
        .filter(([_, v]) => v)
        .map(([k]) => k);
      if (dokumente.length === 0) {
        toast.error("Bitte mindestens ein Dokument auswählen");
        setMissingDocsLoading(false);
        return;
      }
      const r = await axios.post(`${API}/sign-requests/bulk-resend-fehlende-dokumente`, {
        dry_run: true,
        versand_methode: missingDocsMethode,
        dokumente,
        min_kurs_start: missingDocsMinDate || null,
      });
      setMissingDocsPreview(r.data);
      if (r.data.total_betroffen === 0) {
        toast.success("Alle Pflicht-Dokumente liegen bereits signiert vor — nichts zu versenden.");
      }
    } catch (e) {
      toast.error("Vorschau fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setMissingDocsLoading(false);
    }
  };

  const runMissingDocsLive = async () => {
    if (!missingDocsPreview || missingDocsPreview.total_betroffen === 0) return;
    const confirmed = window.confirm(
      `Wirklich ${missingDocsPreview.total_betroffen} Sammelversand-Aktion(en) per ${missingDocsMethode.toUpperCase()} auslösen?\n\n` +
      `Datum auf den Dokumenten: jeweils kurs_start − 1 Tag.\n` +
      `Bei WhatsApp: Du bekommst eine Liste mit wa.me-Links zum Anklicken.`
    );
    if (!confirmed) return;
    setMissingDocsLoading(true);
    try {
      const dokumente = Object.entries(missingDocsTypes)
        .filter(([_, v]) => v)
        .map(([k]) => k);
      const r = await axios.post(`${API}/sign-requests/bulk-resend-fehlende-dokumente`, {
        dry_run: false,
        versand_methode: missingDocsMethode,
        dokumente,
        min_kurs_start: missingDocsMinDate || null,
      });
      setMissingDocsResult(r.data);
      const okCnt = (r.data.results || []).filter(x =>
        ["whatsapp_link_ready","email_sent"].includes(x.status)
      ).length;
      toast.success(`Sammelversand abgeschlossen: ${okCnt} von ${r.data.total_betroffen} erfolgreich.`);
    } catch (e) {
      toast.error("Sammelversand fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setMissingDocsLoading(false);
    }
  };

  // Einzelversand pro TN (aus der Vorschau-Liste)
  const [singleSendLoading, setSingleSendLoading] = useState({});
  const sendMissingDocsForSingleTn = async (tnId, tnName) => {
    setSingleSendLoading(prev => ({ ...prev, [tnId]: true }));
    try {
      const dokumente = Object.entries(missingDocsTypes)
        .filter(([_, v]) => v)
        .map(([k]) => k);
      const r = await axios.post(`${API}/sign-requests/bulk-resend-fehlende-dokumente`, {
        dry_run: false,
        versand_methode: missingDocsMethode,
        dokumente,
        nur_tn_ids: [tnId],
        min_kurs_start: null,  // bei Einzelversand keinen Datums-Filter
      });
      const res = (r.data.results || [])[0];
      if (!res) {
        toast.error("Keine Aktion für " + tnName);
        return;
      }
      if (res.whatsapp_url) {
        window.open(res.whatsapp_url, "_blank");
        toast.success(`WhatsApp für ${tnName} geöffnet`);
      } else if (res.status === "email_sent") {
        toast.success(`E-Mail an ${tnName} versendet`);
      } else {
        toast.error(`${tnName}: ${res.status}`);
      }
      // Eintrag aus Preview-Liste entfernen (UI-Feedback)
      setMissingDocsPreview(prev => prev ? ({
        ...prev,
        total_betroffen: Math.max(0, (prev.total_betroffen || 0) - 1),
        results: (prev.results || []).filter(x => x.teilnehmer_id !== tnId),
      }) : prev);
    } catch (e) {
      toast.error("Versand fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setSingleSendLoading(prev => ({ ...prev, [tnId]: false }));
    }
  };

  // Backfill „Anhang-Flags reparieren"
  const [backfillLoading, setBackfillLoading] = useState(false);

  // === Kurs-Cleanup State ===
  const [kursCleanupOpen, setKursCleanupOpen] = useState(false);
  const [kursCleanupLoading, setKursCleanupLoading] = useState(false);
  const [kursUebersicht, setKursUebersicht] = useState(null);
  const [mergeOverride, setMergeOverride] = useState({}); // { quellName: zielName }
  const [mergePreview, setMergePreview] = useState(null);
  const [hideLegacy, setHideLegacy] = useState(true); // Ninox-Importe ausblenden

  // === Storage-Diagnose State ===
  const [storageOpen, setStorageOpen] = useState(false);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageStatus, setStorageStatus] = useState(null);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [migratePreview, setMigratePreview] = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);

  const loadStorageStatus = async () => {
    setStorageLoading(true);
    try {
      const r = await axios.get(`${API}/azav/storage-status`);
      setStorageStatus(r.data);
    } catch (e) {
      toast.error("Storage-Status fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setStorageLoading(false);
    }
  };

  const runCleanupPreview = async () => {
    try {
      const r = await axios.post(`${API}/azav/storage-cleanup`, { action: "alle", dry_run: true });
      setCleanupPreview(r.data);
    } catch (e) {
      toast.error("Vorschau fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    }
  };

  const confirmCleanup = async () => {
    if (!cleanupPreview) return;
    setCleanupRunning(true);
    try {
      await axios.post(`${API}/azav/storage-cleanup`, { action: "alle", dry_run: false });
      toast.success("Cleanup abgeschlossen.");
      setCleanupPreview(null);
      loadStorageStatus();
    } catch (e) {
      toast.error("Cleanup fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setCleanupRunning(false);
    }
  };

  const runMigratePreview = async () => {
    try {
      const r = await axios.post(`${API}/azav/storage-migrate-pdfs`, { dry_run: true });
      setMigratePreview(r.data);
    } catch (e) {
      toast.error("Migrations-Vorschau fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    }
  };

  const confirmMigrate = async () => {
    if (!migratePreview) return;
    setCleanupRunning(true);
    try {
      const r = await axios.post(`${API}/azav/storage-migrate-pdfs`, { dry_run: false });
      toast.success(`Migration fertig: ${r.data.total_mb_freigegeben} MB ausgelagert`);
      setMigratePreview(null);
      loadStorageStatus();
    } catch (e) {
      toast.error("Migration fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setCleanupRunning(false);
    }
  };

  // NEU: Cloud-Storage-Migration — BATCHED für große Datenmengen (vermeidet 504)
  const runCloudMigration = async () => {
    const confirmed = window.confirm(
      "CLOUD-MIGRATION — schiebt ALLE Anhänge in die sichere Cloud-Speicherung.\n\n" +
      "Bei vielen Dateien (z.B. 487 PDFs) läuft die Migration in mehreren Schritten ab.\n" +
      "Der Prozess kann ein paar Minuten dauern — bitte das Fenster nicht schließen!\n\n" +
      "Fortfahren?"
    );
    if (!confirmed) return;
    
    setCleanupRunning(true);
    const t = toast.loading("Migration läuft — bitte warten...");
    let totalProcessed = 0;
    let totalMB = 0;
    let iteration = 0;
    
    try {
      while (true) {
        iteration++;
        const res = await axios.post(`${API}/azav/migrate-to-object-storage-batch`, { batch_size: 15 }, { timeout: 90000 });
        const d = res.data;
        totalProcessed += d.processed;
        totalMB += d.mb_migrated;
        
        toast.loading(
          `Migration läuft... ${totalProcessed} Dateien fertig, ${d.remaining} verbleibend (${totalMB.toFixed(1)} MB übertragen)`,
          { id: t }
        );
        
        if (d.errors && d.errors.length > 0) {
          console.warn("Migration-Fehler in Batch", iteration, ":", d.errors);
        }
        
        if (d.done) break;
        if (iteration > 100) {
          throw new Error("Sicherheits-Stopp nach 100 Batches");
        }
        // Kleine Pause zwischen Batches
        await new Promise(r => setTimeout(r, 500));
      }
      
      toast.success(
        `Migration komplett! ${totalProcessed} Dateien in der Cloud (${totalMB.toFixed(1)} MB)`,
        { id: t, duration: 10000 }
      );
      loadStorageStatus();
    } catch (e) {
      toast.error(
        `Migration unterbrochen bei ${totalProcessed} Dateien: ${e.response?.data?.detail || e.message}. Du kannst den Button erneut klicken — schon migrierte Dateien werden übersprungen.`,
        { id: t, duration: 12000 }
      );
    } finally {
      setCleanupRunning(false);
    }
  };

  // NEU: Anhänge-Backup ZIP — manueller Download für Diskstation
  const runAnhaengeBackup = async () => {
    const t = toast.loading("Backup wird erzeugt — bitte einen Moment...");
    try {
      // Erst Vorschau zeigen
      const preview = await axios.get(`${API}/backup/anhaenge-preview`);
      toast.dismiss(t);
      const p = preview.data;
      const confirmed = window.confirm(
        `Anhänge-Backup als ZIP\n\n` +
        `Inhalt:\n` +
        `• ${p.gesamt_dateien} Dateien gesamt\n` +
        `• ~${p.geschaetzte_groesse_mb} MB\n\n` +
        `Quellen:\n` +
        `• ${p.quellen.cloud} aus Cloud\n` +
        `• ${p.quellen.filesystem} vom Filesystem\n` +
        `• ${p.quellen.base64} aus DB\n\n` +
        `Struktur:\n` +
        `   Nachname_Vorname/Kategorie/Dateiname.pdf\n\n` +
        `ZIP jetzt herunterladen?`
      );
      if (!confirmed) return;
      
      const t2 = toast.loading("ZIP wird gestreamt — kann ein paar Minuten dauern bei 487 Dateien...");
      const res = await axios.get(`${API}/backup/anhaenge-zip-stream`, {
        responseType: 'blob',
        timeout: 1800000  // 30 Min
      });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-T:]/g, '');
      a.setAttribute('download', `dickies_anhaenge_backup_${ts}.zip`);
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      const sizeMB = (blob.size / 1024 / 1024).toFixed(2);
      toast.success(`${sizeMB} MB Backup heruntergeladen — jetzt auf Diskstation ziehen!`, { id: t2, duration: 10000 });
    } catch (e) {
      toast.error("Backup fehlgeschlagen: " + (e.response?.data?.detail || e.message), { id: t });
    }
  };
  
  // NEU: Wochen-E-Mail konfigurieren
  const [backupEmailsOpen, setBackupEmailsOpen] = useState(false);
  const [backupConfig, setBackupConfig] = useState(null);
  const [newBackupEmail, setNewBackupEmail] = useState("");
  const [newBackupName, setNewBackupName] = useState("");

  // === Synology WebDAV Backup ===
  const [synologyOpen, setSynologyOpen] = useState(false);
  const [synologyConfig, setSynologyConfig] = useState(null);
  const [synologyForm, setSynologyForm] = useState({
    enabled: false,
    base_url: "",
    username: "",
    password: "",
    target_folder: "/CRM-Backup",
    verify_tls: false,
    max_keep: 4,
  });
  const [synologyTesting, setSynologyTesting] = useState(false);
  const [synologyTestResult, setSynologyTestResult] = useState(null);
  const [synologyPushing, setSynologyPushing] = useState(false);
  const [synologyPushResult, setSynologyPushResult] = useState(null);

  const loadSynologyConfig = async () => {
    try {
      const r = await axios.get(`${API}/backup/synology-config`);
      setSynologyConfig(r.data);
      if (r.data?.configured) {
        setSynologyForm({
          enabled: r.data.enabled || false,
          base_url: r.data.base_url || "",
          username: r.data.username || "",
          password: "",
          target_folder: r.data.target_folder || "/CRM-Backup",
          verify_tls: r.data.verify_tls !== false,
          max_keep: r.data.max_keep || 4,
        });
      }
      setSynologyTestResult(null);
      setSynologyPushResult(null);
    } catch (e) {
      toast.error("Synology-Config konnte nicht geladen werden");
    }
  };

  const saveSynologyConfig = async () => {
    try {
      const r = await axios.put(`${API}/backup/synology-config`, synologyForm);
      setSynologyConfig(r.data);
      setSynologyForm(f => ({ ...f, password: "" }));
      toast.success("Synology-Konfiguration gespeichert");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    }
  };

  const testSynologyConnection = async () => {
    setSynologyTesting(true);
    setSynologyTestResult(null);
    try {
      await axios.put(`${API}/backup/synology-config`, synologyForm);
      const r = await axios.post(`${API}/backup/synology-test`);
      setSynologyTestResult(r.data);
      if (r.data?.ok) toast.success("Verbindung zur Synology OK");
      else toast.error("" + (r.data?.error || "Verbindung fehlgeschlagen"));
    } catch (e) {
      const err = e.response?.data?.detail || e.message;
      setSynologyTestResult({ ok: false, error: err });
      toast.error("Test fehlgeschlagen: " + err);
    } finally {
      setSynologyTesting(false);
    }
  };

  const pushSynologyNow = async () => {
    if (!window.confirm("Backup jetzt direkt auf die Synology hochladen?\n(Kann je nach Anhangsmenge 1–5 Min dauern)")) return;
    setSynologyPushing(true);
    setSynologyPushResult(null);
    const t = toast.loading("Backup wird auf Synology hochgeladen...");
    try {
      const r = await axios.post(`${API}/backup/synology-push-now`, {});
      setSynologyPushResult(r.data);
      if (r.data?.ok) {
        toast.success(`${r.data.bytes ? (r.data.bytes / 1024 / 1024).toFixed(1) : '?'} MB erfolgreich auf Synology gesichert!`, { id: t, duration: 8000 });
        loadSynologyConfig();
      } else {
        toast.error("" + (r.data?.error || "Upload fehlgeschlagen"), { id: t });
      }
    } catch (e) {
      toast.error("Fehler: " + (e.response?.data?.detail || e.message), { id: t });
    } finally {
      setSynologyPushing(false);
    }
  };

  // === Kurszuordnung (Ninox-TN ohne Kurs) ===
  const [kursZuordnenOpen, setKursZuordnenOpen] = useState(false);
  const [ohneKursLoading, setOhneKursLoading] = useState(false);
  const [ohneKursList, setOhneKursList] = useState([]);
  const [ohneKursFilter, setOhneKursFilter] = useState({ source: "", nur_bg: false });
  const [zuordnung, setZuordnung] = useState({});  // {tn_id: kurs_name}
  const [zuordnenLoading, setZuordnenLoading] = useState({});  // {tn_id: bool}

  const loadOhneKurs = async () => {
    setOhneKursLoading(true);
    try {
      const params = new URLSearchParams();
      if (ohneKursFilter.source) params.append("source", ohneKursFilter.source);
      if (ohneKursFilter.nur_bg) params.append("nur_bg", "true");
      const r = await axios.get(`${API}/azav-mappe/ohne-kurs?${params.toString()}`);
      setOhneKursList(r.data?.teilnehmer || []);
      setZuordnung({});
    } catch (e) {
      toast.error("Laden fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setOhneKursLoading(false);
    }
  };

  const zuordnenSingle = async (tnId, kursName) => {
    if (!kursName) {
      toast.error("Bitte erst einen Kurs auswählen");
      return;
    }
    setZuordnenLoading(prev => ({ ...prev, [tnId]: true }));
    try {
      await axios.post(`${API}/azav-mappe/kurs-zuordnen/${tnId}`, { kurs_name: kursName });
      toast.success(`${kursName} zugeordnet`);
      // TN aus Liste entfernen
      setOhneKursList(list => list.filter(t => t.id !== tnId));
      setZuordnung(prev => { const n = { ...prev }; delete n[tnId]; return n; });
      // AZAV-Mappe-Hauptdaten neu laden damit die Änderung sichtbar wird
      fetchData();
    } catch (e) {
      toast.error("Fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setZuordnenLoading(prev => ({ ...prev, [tnId]: false }));
    }
  };

  const zuordnenBulk = async () => {
    const eintraege = Object.entries(zuordnung).filter(([_, v]) => v);
    if (eintraege.length === 0) {
      toast.error("Keine Zuordnungen vorgenommen");
      return;
    }
    if (!window.confirm(`${eintraege.length} Teilnehmer auf einmal zuordnen?`)) return;
    
    const t = toast.loading(`Zuordnung läuft (0/${eintraege.length})...`);
    let ok = 0, fail = 0;
    for (let i = 0; i < eintraege.length; i++) {
      const [tnId, kursName] = eintraege[i];
      try {
        await axios.post(`${API}/azav-mappe/kurs-zuordnen/${tnId}`, { kurs_name: kursName });
        ok++;
        setOhneKursList(list => list.filter(t => t.id !== tnId));
      } catch (e) {
        fail++;
      }
      toast.loading(`Zuordnung läuft (${i + 1}/${eintraege.length})...`, { id: t });
    }
    toast.success(`${ok} zugeordnet${fail > 0 ? `, ${fail} Fehler` : ''}`, { id: t });
    setZuordnung({});
    fetchData();
  };

  // === Wochen-E-Mail ===  
  const loadBackupConfig = async () => {
    try {
      const r = await axios.get(`${API}/backup/anhaenge-config`);
      setBackupConfig(r.data);
    } catch (e) {
      toast.error("Backup-Config konnte nicht geladen werden");
    }
  };
  
  const saveBackupConfig = async (emails) => {
    try {
      await axios.put(`${API}/backup/anhaenge-config`, { emails });
      toast.success("Backup-Empfänger gespeichert");
      loadBackupConfig();
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    }
  };
  
  const sendBackupNow = async (email, name) => {
    const t = toast.loading(`Backup wird an ${email} versendet...`);
    try {
      const r = await axios.post(`${API}/backup/anhaenge-email-now`, { email, name });
      toast.success(`${r.data.files} Dateien (${r.data.size_mb} MB) an ${email} verschickt`, { id: t, duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen", { id: t });
    }
  };
  
  // NEU: Notfall-Cleanup — alle Aktionen in einem Schritt
  const runNotfallCleanup = async () => {
    const confirmed = window.confirm(
      "NOTFALL-CLEANUP — wird ALLES auf einmal ausführen:\n\n" +
      "1. Doppelte Anhänge entfernen\n" +
      "2. Alte Sign-Requests (>6 Monate) löschen\n" +
      "3. DB-Snapshots reduzieren (nur 2 behalten)\n" +
      "4. E-Mail-Log älter 90 Tage löschen\n" +
      "5. ALLE PDFs aus DB ins Filesystem auslagern\n\n" +
      "Vor dem echten Lauf bekommst du eine Vorschau.\nFortfahren?"
    );
    if (!confirmed) return;
    
    setCleanupRunning(true);
    const t = toast.loading("Vorschau wird erstellt...");
    try {
      // Erst Dry-Run
      const preview = await axios.post(`${API}/azav/notfall-cleanup`, { dry_run: true });
      toast.dismiss(t);
      const d = preview.data;
      const summary = 
        `Vorschau:\n\n` +
        `• ${d.gesamt_eintraege_geloescht} alte Einträge werden gelöscht\n` +
        `• ${d.gesamt_mb_freigegeben} MB PDFs werden ausgelagert\n\n` +
        `Jetzt ECHT ausführen?`;
      
      if (!window.confirm(summary)) {
        setCleanupRunning(false);
        return;
      }
      
      const t2 = toast.loading("Notfall-Cleanup läuft — das kann 1-2 Minuten dauern...");
      const result = await axios.post(`${API}/azav/notfall-cleanup`, { dry_run: false });
      const r = result.data;
      toast.success(
        `Cleanup fertig! ${r.gesamt_eintraege_geloescht} Einträge gelöscht, ` +
        `${r.gesamt_mb_freigegeben} MB ausgelagert. ` +
        (r.db_size_mb_nach_cleanup ? `DB jetzt: ${r.db_size_mb_nach_cleanup} MB` : ''),
        { id: t2, duration: 8000 }
      );
      loadStorageStatus();
    } catch (e) {
      toast.error("Notfall-Cleanup fehlgeschlagen: " + (e.response?.data?.detail || e.message), { id: t });
    } finally {
      setCleanupRunning(false);
    }
  };

  const loadKursUebersicht = async () => {
    setKursCleanupLoading(true);
    try {
      const r = await axios.get(`${API}/azav/kurs-uebersicht`);
      setKursUebersicht(r.data);
    } catch (e) {
      toast.error("Übersicht laden fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setKursCleanupLoading(false);
    }
  };

  // Schritt 1: Vorschau laden (dry-run) → Liste der betroffenen TN anzeigen
  const startMergePreview = async (von, nach) => {
    if (!nach || von === nach) return;
    try {
      const r = await axios.post(`${API}/azav/kurs-merge`, { von, nach, dry_run: true });
      setMergePreview(r.data);
    } catch (e) {
      toast.error("Vorschau fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    }
  };

  // Schritt 2: Tatsächlich mergen (nach User-Bestätigung in der Preview)
  const confirmMerge = async () => {
    if (!mergePreview) return;
    try {
      const r = await axios.post(`${API}/azav/kurs-merge`, {
        von: mergePreview.von,
        nach: mergePreview.nach,
        dry_run: false,
      });
      toast.success(`Merge abgeschlossen: ${r.data.tn_hauptkurs_modified + r.data.tn_kurse_array_modified} Einträge geändert`);
      setMergePreview(null);
      loadKursUebersicht();
    } catch (e) {
      toast.error("Merge fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    }
  };

  const runBackfillFlags = async () => {
    const ok = window.confirm(
      `Reparatur „Vorhanden/Fehlt"-Markierungen\n\n` +
      `Geht alle Teilnehmer durch und setzt fehlende Flags ` +
      `(Bildungsgutschein/Maßnahmevertrag/Praktikumsvertrag/Datenschutz) ` +
      `wenn der Anhang im System bereits hochgeladen ist.\n\n` +
      `Wird jetzt ausgeführt?`
    );
    if (!ok) return;
    setBackfillLoading(true);
    try {
      const res = await axios.post(`${API}/azav/backfill-anhang-flags?dry_run=false`);
      const n = res.data.total_updated || 0;
      if (n === 0) {
        toast.info("Alles bereits konsistent — keine Reparatur nötig.");
      } else {
        toast.success(`${n} Teilnehmer repariert.`);
      }
      fetchData(); // Liste neu laden
    } catch (e) {
      toast.error("Reparatur fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setBackfillLoading(false);
    }
  };

  const runSelfAudit = async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      if (auditFilter.kurs) params.set("kurs", auditFilter.kurs);
      if (auditFilter.bg_sz) params.set("bg_sz", auditFilter.bg_sz);
      if (auditFilter.bestanden) params.set("bestanden", auditFilter.bestanden);
      if (auditFilter.abgebrochen) params.set("abgebrochen", auditFilter.abgebrochen);
      if (auditFilter.jahr) params.set("jahr", auditFilter.jahr);
      const res = await axios.get(`${API}/azav/self-audit?${params.toString()}`);
      setAuditReport(res.data);
      toast.success(`Self-Audit: ${res.data.issues_total} Auffälligkeit(en) (${res.data.ampel.toUpperCase()})`);
    } catch (e) {
      toast.error("Self-Audit fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setAuditLoading(false);
    }
  };

  // === Duplikate aufräumen ===
  const [cleanupDupLoading, setCleanupDupLoading] = useState(false);
  const cleanupDuplicates = async () => {
    setCleanupDupLoading(true);
    try {
      // Schritt 1: Dry-Run für Vorschau
      const preview = await axios.post(`${API}/azav/cleanup-duplicates`, { dry_run: true });
      const planned = preview.data.anzahl_aktionen_geplant || 0;
      if (planned === 0) {
        toast.success("Keine echten Duplikate gefunden — alles sauber!");
        return;
      }
      const confirmed = window.confirm(
        `${planned} Duplikat-Dateien gefunden.\n\n` +
        `Pro Teilnehmer+Kategorie wird die NEUESTE Datei behalten, ältere werden soft-deleted ` +
        `(rückgängig machbar bei Cloud-Storage).\n\n` +
        `Wirklich ausführen?`
      );
      if (!confirmed) return;
      // Schritt 2: Live-Lauf
      const r = await axios.post(`${API}/azav/cleanup-duplicates`, {
        dry_run: false,
        max_aktionen: 2000,
      });
      toast.success(`${r.data.anzahl_geloescht} Duplikate entfernt`);
      // Audit neu laden, damit Anzeige aktualisiert ist
      runSelfAudit();
    } catch (e) {
      toast.error("Cleanup fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setCleanupDupLoading(false);
    }
  };

  const runBulkResend = async () => {
    setResendLoading(true);
    try {
      const res = await axios.post(`${API}/sign-requests/bulk-resend-erstgespraech`, {
        dry_run: resendDryRun,
        versand_methode_default: resendMethode,
      });
      setResendResult(res.data);
      if (resendDryRun) {
        toast.info(`${res.data.total_candidates} Kandidat(en) gefunden (Dry-Run)`);
      } else {
        toast.success(`${res.data.total_candidates} Sign-Requests neu erstellt`);
      }
    } catch (e) {
      toast.error("Bulk-Resend fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setResendLoading(false);
    }
  };

  const downloadAuditJson = () => {
    if (!auditReport) return;
    const blob = new Blob([JSON.stringify(auditReport, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `azav_self_audit_${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAuditExcel = async () => {
    if (!auditReport) return;
    // Pre-Export-Check: fehlende Signaturen?
    const fehlendeSigs = auditReport.issues?.fehlende_signaturen?.count || 0;
    if (fehlendeSigs > 0) {
      const ok = window.confirm(
        `ACHTUNG: ${fehlendeSigs} Dokument(e) ohne Signatur gefunden.\n\n` +
        `Du kannst den Audit-Export trotzdem ziehen, solltest die fehlenden ` +
        `Unterschriften aber VOR dem AZAV-Audit einholen.\n\n` +
        `Trotzdem exportieren?`
      );
      if (!ok) return;
    }
    try {
      const params = new URLSearchParams();
      if (auditFilter.kurs) params.set("kurs", auditFilter.kurs);
      if (auditFilter.bg_sz) params.set("bg_sz", auditFilter.bg_sz);
      if (auditFilter.bestanden) params.set("bestanden", auditFilter.bestanden);
      if (auditFilter.abgebrochen) params.set("abgebrochen", auditFilter.abgebrochen);
      if (auditFilter.jahr) params.set("jahr", auditFilter.jahr);
      const res = await axios.get(`${API}/azav/self-audit-export?${params.toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `azav_self_audit_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel-Export gestartet");
    } catch (e) {
      toast.error("Excel-Export fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/azav-mappe`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching AZAV data:", error);
      toast.error("Fehler beim Laden der AZAV-Daten");
    } finally {
      setLoading(false);
    }
  };

  // NEU: AZAV-Selbstaudit-PDF generieren (professioneller Bericht für Auditor)
  const downloadAuditPdf = async () => {
    const t = toast.loading("AZAV-Audit-Bericht wird erzeugt...");
    try {
      const params = new URLSearchParams();
      if (auditFilter?.kurs) params.append("kurs", auditFilter.kurs);
      if (auditFilter?.bg_sz) params.append("bg_sz", auditFilter.bg_sz);
      if (auditFilter?.bestanden) params.append("bestanden", auditFilter.bestanden);
      if (auditFilter?.abgebrochen) params.append("abgebrochen", auditFilter.abgebrochen);
      if (auditFilter?.jahr) params.append("jahr", auditFilter.jahr);
      const res = await axios.get(`${API}/azav/self-audit-pdf?${params.toString()}`, { responseType: "blob", timeout: 120000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.setAttribute("download", `azav_selbstaudit_${ts}.pdf`);
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("AZAV-Audit-Bericht heruntergeladen", { id: t, duration: 6000 });
    } catch (e) {
      toast.error("PDF-Bericht fehlgeschlagen: " + (e.response?.data?.detail || e.message), { id: t });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/azav-mappe/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AZAV_Mappe_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim PDF-Download");
    }
  };

  const handleTeilnehmerClick = (teilnehmerId) => {
    setSelectedTeilnehmerId(teilnehmerId);
    setDetailOpen(true);
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setSelectedTeilnehmerId(null);
    // Refresh data after closing detail
    fetchData();
  };

  const filteredTeilnehmer = data.teilnehmer.filter(t => {
    // Such-Filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      (t.vorname || '').toLowerCase().includes(searchLower) ||
      (t.nachname || '').toLowerCase().includes(searchLower) ||
      (t.kurs || '').toLowerCase().includes(searchLower) ||
      (t.bildungsgutschein_nummer || '').toLowerCase().includes(searchLower)
    );
    if (!matchesSearch) return false;

    // Jahr-Filter: TN dem Jahr zuordnen, in dem er startet ODER endet
    if (jahrFilter !== "all") {
      const start = (t.kurs_start || "").slice(0, 4);
      const ende = (t.kurs_ende || "").slice(0, 4);
      if (start !== jahrFilter && ende !== jahrFilter) return false;
    }
    return true;
  });

  const StatusIcon = ({ ok, title }) => ok ? 
    <CheckCircle className="text-green-500" size={18} title={title} /> : 
    <XCircle className="text-red-400" size={18} title={title} />;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString('de-DE');
    } catch {
      return dateStr;
    }
  };

  // Berechne AZAV-Vollständigkeit
  const getAzavProgress = (t) => {
    const checks = [
      t.azav_status?.hat_bg || t.bildungsgutschein_vorhanden,
      t.azav_status?.hat_erstgespraech || t.erstgespraech_datum,
      t.azav_status?.hat_datenschutz || t.datenschutz_vorhanden,
      t.vertrag || t.azav_status?.hat_vertrag,
      t.azav_status?.hat_pruefung || t.pruefung_am,
      t.azav_status?.bestanden || t.bestanden
    ];
    const done = checks.filter(Boolean).length;
    return { done, total: checks.length, percent: Math.round((done / checks.length) * 100) };
  };

  // Kurs-Info ermitteln
  const getKursInfo = (kurs) => {
    if (!kurs) return null;
    for (const [name, info] of Object.entries(KURS_INFO)) {
      if (kurs.toLowerCase().includes(name.toLowerCase()) || 
          name.toLowerCase().includes(kurs.toLowerCase())) {
        return { name, ...info };
      }
    }
    return null;
  };

  return (
    <div className="page-container" data-testid="azav-mappe-page">
      <div className={modus === "mappe" ? "flex items-center justify-between mb-6" : "flex flex-col gap-4 mb-6"}>
        <div>
          <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="azav-seite-titel">
            {modus === "mappe" ? "AZAV-Mappe" : "Datensicherung & Wartung"}
          </h1>
          <p className="text-sm text-[#78716c] mt-1">
            {modus === "mappe"
              ? "Bildungsgutschein-Teilnehmer für AZAV-Zertifizierung • Klicken Sie auf einen Teilnehmer für Details"
              : "Technische Werkzeuge – ausgelagert aus der AZAV-Mappe"}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {modus === "mappe" && (<>
          <Button 
            variant="outline" 
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          <Button
            variant="outline"
            className="border-stone-300 text-stone-700 hover:bg-stone-50"
            onClick={() => setQmDokumenteOpen(true)}
            data-testid="qm-dokumente-btn"
            title="QM-Dokumentenliste (NW_QM_03) — alle Formblätter, Nachweise und Verfahrensanweisungen"
          >
            <FileText size={16} className="mr-2" />
            QM-Dokumente
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await axios.get(`${API}/admin/klaerungsliste-bg-dubletten/pdf`, { responseType: "blob" });
                window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
              } catch (e) { toast.error("Klärungsliste konnte nicht erstellt werden"); }
            }}
            data-testid="klaerungsliste-btn"
          >
            <FileText size={16} className="mr-2" />
            Klärungsliste BG & Doppelte
          </Button>
          </>)}
          {!isReadOnly && modus === "mappe" && (
          <>
          <Button
            variant="outline"
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            onClick={() => { setAuditOpen(true); setAuditReport(null); }}
            data-testid="self-audit-btn"
          >
            <Shield size={16} className="mr-2" />
            Self-Audit
          </Button>
          <Button
            variant="outline"
            className="border-indigo-400 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 font-medium"
            onClick={() => { setKursZuordnenOpen(true); loadOhneKurs(); }}
            data-testid="kurs-zuordnen-btn"
            title="Teilnehmer ohne Kursname → hier manuell zuordnen (z.B. Ninox-Importe)"
          >
            <BookOpen size={16} className="mr-2" />
            Kurse zuordnen
          </Button>
          <Button
            variant="outline"
            className="border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 font-medium"
            onClick={() => { setMissingDocsOpen(true); setMissingDocsPreview(null); setMissingDocsResult(null); }}
            data-testid="missing-docs-bulk-btn"
            title="Sammelversand: alle fehlenden Maßnahmevertrag/Datenschutz/Erstgespräch in einem Schwung"
          >
            <Mail size={16} className="mr-2" />
            Fehlende Doks senden
          </Button>
          </>
          )}
          {!isReadOnly && modus === "werkzeuge" && (
          <>
          <div className="w-full mt-2">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500">Datensicherung</h2>
            <p className="text-xs text-stone-500">Sicherungen der Unterlagen und Speicher-Kontrolle</p>
          </div>
          <Button
            variant="outline"
            className="border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold"
            onClick={runCloudMigration}
            disabled={cleanupRunning}
            data-testid="cloud-migration-btn"
            title="Alle Anhänge dauerhaft in der Emergent Cloud sichern — überlebt Re-Deploys!"
          >
            <Database size={16} className="mr-2" />
            In Cloud sichern
          </Button>
          <Button
            variant="outline"
            className="border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold"
            onClick={runAnhaengeBackup}
            data-testid="anhaenge-backup-btn"
            title="Lade alle Anhänge als ZIP herunter — ideal für Diskstation/Backup-Festplatte"
          >
            <Download size={16} className="mr-2" />
            ZIP-Backup
          </Button>
          <Button
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={() => { setBackupEmailsOpen(true); loadBackupConfig(); }}
            data-testid="backup-email-config-btn"
            title="Automatische Wochen-E-Mail mit Backup einrichten"
          >
            <Mail size={16} className="mr-2" />
            Wochen-E-Mail
          </Button>
          <Button
            variant="outline"
            className="border-sky-400 bg-sky-50 text-sky-800 hover:bg-sky-100 font-medium"
            onClick={() => { setSynologyOpen(true); loadSynologyConfig(); }}
            data-testid="synology-backup-btn"
            title="Direkter Backup-Push auf deine Synology Diskstation per WebDAV"
          >
            <Database size={16} className="mr-2" />
            Synology-Backup
          </Button>
          <Button
            variant="outline"
            className="border-rose-300 text-rose-800 hover:bg-rose-50"
            onClick={() => { setStorageOpen(true); loadStorageStatus(); }}
            data-testid="storage-btn"
          >
            <AlertCircle size={16} className="mr-2" />
            Storage prüfen
          </Button>
          <Button
            variant="outline"
            className="border-red-500 bg-red-50 text-red-700 hover:bg-red-100 font-bold"
            onClick={runNotfallCleanup}
            disabled={cleanupRunning}
            data-testid="notfall-cleanup-btn"
            title="Atlas-Speicher voll? Führt alle Aufräum-Aktionen in einem Klick aus."
          >
            <AlertCircle size={16} className="mr-2" />
            Notfall-Cleanup
          </Button>
          {user?.role === "gf" && (
          <>
          <div className="w-full mt-2">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500">Wartung (nur Geschäftsführung)</h2>
            <p className="text-xs text-stone-500">Einmalige Reparaturen – im Alltag nicht nötig. Jede Aktion fragt vorher nach.</p>
          </div>
          <Button
            variant="outline"
            className="border-rose-300 text-rose-800 hover:bg-rose-50"
            onClick={() => setAnhangRepairOpen(true)}
            data-testid="anhang-repair-btn"
            title="Migriert Inline-Anhänge & Sonderfeld-Anhänge in die saubere Collection — behebt 'Sonstige 1.1.1970'"
          >
            <Wrench size={16} className="mr-2" />
            Anhang-Reparatur
          </Button>
          <Button
            variant="outline"
            className="border-blue-300 text-blue-800 hover:bg-blue-50"
            onClick={() => { setResendOpen(true); setResendResult(null); }}
            data-testid="bulk-resend-btn"
          >
            <Mail size={16} className="mr-2" />
            Erstgespräche neu senden
          </Button>
          <Button
            variant="outline"
            className="border-orange-400 bg-orange-50 text-orange-800 hover:bg-orange-100 font-medium"
            onClick={async () => {
              try {
                toast.info("Prüfe Kandidaten…");
                const dry = await axios.post(`${API}/teilnehmer/erstgespraech-bulk-regenerate`, {
                  dry_run: true, only_missing_signature: true,
                });
                const ids = (dry.data?.regenerated || []).map(x => x.teilnehmer_id).filter(Boolean);
                const total = ids.length;
                if (total === 0) {
                  toast.success("Keine EG-Bögen ohne Signatur gefunden — alles sauber.");
                  return;
                }
                const namen = (dry.data?.regenerated || [])
                  .map(x => x.name || x.teilnehmer_id.slice(0, 8)).slice(0, 6).join(", ");
                if (!window.confirm(
                  `${total} Erstgesprächsbogen ohne Signatur gefunden:\n\n` +
                  `${namen}${total > 6 ? " …" : ""}\n\n` +
                  `Jetzt neu generieren (mit MD-Unterschrift + Stempel)?`
                )) return;

                const BATCH = 3;
                let done = 0, errors = 0;
                const toastId = toast.loading(`Regeneriere EG-Bögen … 0 / ${total}`);
                for (let i = 0; i < ids.length; i += BATCH) {
                  const chunk = ids.slice(i, i + BATCH);
                  try {
                    const r = await axios.post(
                      `${API}/teilnehmer/erstgespraech-bulk-regenerate`,
                      { dry_run: false, only_missing_signature: false, teilnehmer_ids: chunk },
                      { timeout: 60000 }
                    );
                    done += r.data?.regenerated_count || 0;
                    errors += (r.data?.errors || []).length;
                  } catch (e) {
                    errors += chunk.length;
                  }
                  toast.loading(`Regeneriere EG-Bögen … ${done} / ${total}`, { id: toastId });
                }
                toast.dismiss(toastId);
                toast.success(
                  `${done} / ${total} EG-Bögen erfolgreich neu erzeugt` +
                  (errors ? ` · ${errors} Fehler` : "")
                );
              } catch (e) {
                toast.error("EG-Regenerierung fehlgeschlagen",
                  { description: e?.response?.data?.detail || e.message });
              }
            }}
            data-testid="eg-bulk-regenerate-btn"
            title="Regeneriert alle Erstgesprächsbögen mit MD-Unterschrift & Stempel (Batches à 3, Timeout-sicher)"
          >
            <RefreshCw size={16} className="mr-2" />
            EG neu unterschreiben
          </Button>
          <Button
            variant="outline"
            className="border-blue-400 bg-blue-50 text-blue-800 hover:bg-blue-100 font-medium"
            onClick={async () => {
              try {
                // 1) Kandidaten ermitteln
                toast.info("Prüfe Kandidaten…");
                const alle = await axios.post(`${API}/teilnehmer/datenschutz-bulk-regenerate`, {
                  dry_run: true, include_without_ds: true,
                });
                const ids = (alle.data?.regenerated || []).map(x => x.teilnehmer_id).filter(Boolean);
                const total = ids.length;
                if (total === 0) {
                  toast.success("Keine Teilnehmer gefunden.");
                  return;
                }
                if (!window.confirm(
                  `${total} Datenschutz-PDF(s) werden neu erzeugt (inkl. aktueller Einwilligungs-Häkchen).\n\n` +
                  `Verarbeitung in Batches (Timeout-sicher). Fortschritt siehst du oben.`
                )) return;

                // 2) In Batches à 3 verarbeiten (jedes PDF ~2s → Batch < 10s, Proxy-safe)
                const BATCH = 3;
                let done = 0, errors = 0;
                const toastId = toast.loading(`Regeneriere DS-PDFs … 0 / ${total}`);
                for (let i = 0; i < ids.length; i += BATCH) {
                  const chunk = ids.slice(i, i + BATCH);
                  try {
                    const r = await axios.post(
                      `${API}/teilnehmer/datenschutz-bulk-regenerate`,
                      { dry_run: false, include_without_ds: true, teilnehmer_ids: chunk },
                      { timeout: 60000 }
                    );
                    done += r.data?.regenerated_count || 0;
                    errors += (r.data?.errors || []).length;
                  } catch (e) {
                    errors += chunk.length;
                  }
                  toast.loading(`Regeneriere DS-PDFs … ${done} / ${total}`, { id: toastId });
                }
                toast.dismiss(toastId);
                toast.success(
                  `${done} / ${total} Datenschutz-PDFs neu erzeugt` +
                  (errors ? ` · ${errors} Fehler` : "")
                );
              } catch (e) {
                toast.error("Datenschutz-Regenerierung fehlgeschlagen",
                  { description: e?.response?.data?.detail || e.message });
              }
            }}
            data-testid="ds-bulk-regenerate-btn"
            title="Regeneriert alle Datenschutz-PDFs (FB_TN_03) mit aktuellen Einwilligungs-Häkchen"
          >
            <RefreshCw size={16} className="mr-2" />
            Datenschutz neu drucken
          </Button>
          <Button
            variant="outline"
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            onClick={runBackfillFlags}
            disabled={backfillLoading}
            data-testid="backfill-flags-btn"
            title='Repariert "Fehlt"-Markierungen bei Teilnehmern, deren Anhänge bereits hochgeladen sind'
          >
            {backfillLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle size={16} className="mr-2" />}
            Status reparieren
          </Button>
          <Button
            variant="outline"
            className="border-purple-300 text-purple-800 hover:bg-purple-50"
            onClick={() => { setKursCleanupOpen(true); loadKursUebersicht(); }}
            data-testid="kurs-cleanup-btn"
          >
            <BookOpen size={16} className="mr-2" />
            Kurse aufräumen
          </Button>
          <Button
            variant="outline"
            className="border-purple-400 bg-purple-50 text-purple-800 hover:bg-purple-100 font-medium"
            onClick={async () => {
              if (!window.confirm(
                "Datenschutz-Vollreparatur startet:\n\n" +
                "1) Alle 3 Einwilligungen (Fotos, WhatsApp, WA-Gruppe) für alle TN auf JA setzen\n" +
                "   (nur wo noch nicht gesetzt — bereits widerrufene bleiben unberührt)\n" +
                "2) Alle Datenschutz-PDFs neu erzeugen (mit Kreuzen)\n" +
                "3) Alle Erstgesprächsbögen neu erzeugen (mit MD-Signatur)\n\n" +
                "Fortsetzen?"
              )) return;

              if (!window.confirm(
                "Datenschutz-Vollreparatur startet — GEFILTERT auf:\n" +
                "  • AzAV-Teilnehmer (Bildungsgutschein)\n" +
                "  • Kursjahr 2026\n\n" +
                "0) Referenz-Unterschriften aus signierten MV/EG-PDFs extrahieren (Backfill)\n" +
                "1) Einwilligungen (Fotos, WhatsApp, WA-Gruppe) auf JA setzen (wo noch nicht gesetzt)\n" +
                "2) Alle Datenschutz-PDFs neu erzeugen (mit Kreuzen + TN-Unterschrift)\n" +
                "3) Alle Erstgesprächsbögen neu erzeugen (mit MD-Signatur + TN-Unterschrift)\n\n" +
                "Fortsetzen?"
              )) return;

              const AZAV_FILTER = { only_azav: true, year: 2026 };
              const toastId = toast.loading("Schritt 0/3: Signatur-Backfill…");
              let backfillCount = 0;
              try {
                const bf = await axios.post(
                  `${API}/teilnehmer/backfill-referenz-signatur`,
                  { dry_run: false, overwrite: false, ...AZAV_FILTER },
                  { timeout: 90000 }
                );
                backfillCount = bf.data?.updated_count || 0;
              } catch (e) {
                console.error("Schritt 0 (Backfill) fehlgeschlagen — mache trotzdem weiter:", e);
              }

              toast.loading("Schritt 1/3: Einwilligungen setzen…", { id: toastId });
              let setCount = 0;
              try {
                const setRes = await axios.post(
                  `${API}/teilnehmer/einwilligungen-bulk-set`,
                  { fotos: true, whatsapp: true, whatsapp_gruppe: true, dry_run: false, ...AZAV_FILTER },
                  { timeout: 90000 }
                );
                setCount = setRes.data?.updated_count || 0;
              } catch (e) {
                console.error("Schritt 1 fehlgeschlagen — mache trotzdem weiter:", e);
                toast.error("Schritt 1 (Einwilligungen) fehlgeschlagen — mache mit PDF-Regen weiter", {
                  description: e?.message || "Timeout",
                  duration: 5000,
                });
              }

              try {
                toast.loading(`Schritt 2/3: DS-PDFs vorbereiten…`, { id: toastId });
                const dsDry = await axios.post(
                  `${API}/teilnehmer/datenschutz-bulk-regenerate`,
                  { dry_run: true, include_without_ds: true, ...AZAV_FILTER },
                  { timeout: 90000 }
                );
                const dsIds = (dsDry.data?.regenerated || []).map(x => x.teilnehmer_id).filter(Boolean);
                const dsTotal = dsIds.length;
                let dsDone = 0, dsErr = 0;
                const BATCH = 3;
                for (let i = 0; i < dsIds.length; i += BATCH) {
                  const chunk = dsIds.slice(i, i + BATCH);
                  try {
                    const r = await axios.post(
                      `${API}/teilnehmer/datenschutz-bulk-regenerate`,
                      { dry_run: false, include_without_ds: true, teilnehmer_ids: chunk },
                      { timeout: 90000 }
                    );
                    dsDone += r.data?.regenerated_count || 0;
                    dsErr += (r.data?.errors || []).length;
                  } catch (e) { dsErr += chunk.length; }
                  toast.loading(`Schritt 2/3: DS-PDFs … ${dsDone} / ${dsTotal}`, { id: toastId });
                }

                toast.loading("Schritt 3/3: EG-Bögen prüfen…", { id: toastId });
                const egDry = await axios.post(
                  `${API}/teilnehmer/erstgespraech-bulk-regenerate`,
                  { dry_run: true, only_missing_signature: true, ...AZAV_FILTER },
                  { timeout: 90000 }
                );
                const egIds = (egDry.data?.regenerated || []).map(x => x.teilnehmer_id).filter(Boolean);
                const egTotal = egIds.length;
                let egDone = 0, egErr = 0;
                for (let i = 0; i < egIds.length; i += BATCH) {
                  const chunk = egIds.slice(i, i + BATCH);
                  try {
                    const r = await axios.post(
                      `${API}/teilnehmer/erstgespraech-bulk-regenerate`,
                      { dry_run: false, only_missing_signature: false, teilnehmer_ids: chunk },
                      { timeout: 90000 }
                    );
                    egDone += r.data?.regenerated_count || 0;
                    egErr += (r.data?.errors || []).length;
                  } catch (e) { egErr += chunk.length; }
                  toast.loading(`Schritt 3/3: EG-Bögen … ${egDone} / ${egTotal}`, { id: toastId });
                }

                toast.dismiss(toastId);
                toast.success(
                  `Vollreparatur abgeschlossen`,
                  {
                    description:
                      `Signaturen extrahiert: ${backfillCount} · Einwilligungen: ${setCount} · DS-PDFs: ${dsDone}/${dsTotal}` +
                      (dsErr ? ` (${dsErr} Fehler)` : "") +
                      ` · EG-Bögen: ${egDone}/${egTotal}` +
                      (egErr ? ` (${egErr} Fehler)` : ""),
                    duration: 15000,
                  }
                );
              } catch (e) {
                toast.dismiss(toastId);
                toast.error("Vollreparatur teilweise fehlgeschlagen",
                  { description: e?.response?.data?.detail || e.message });
              }
            }}
            data-testid="datenschutz-vollreparatur-btn"
            title="AzAV 2026: Setzt Einwilligungen für alle BG-TN im Kursjahr 2026 + regeneriert DS-PDFs + regeneriert EG-Bögen"
          >
            <Zap size={16} className="mr-2" />
            Datenschutz-Vollreparatur – Achtung: setzt Einwilligungen auf JA
          </Button>
          </>
          )}
          </>
          )}
          {modus === "mappe" && (
          <Button 
            className="bg-[#D9775A] hover:bg-[#C2603F] text-white"
            onClick={handleDownloadPDF}
            data-testid="download-pdf-btn"
          >
            <Download size={16} className="mr-2" />
            PDF Export
          </Button>
          )}
        </div>
      </div>

      {modus === "mappe" && (<>
      {user?.role === "gf" && <FinanzierungRueckfall />}
      {user?.role === "gf" && <DublettenZusammenfuehren />}
      {/* Statistik Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="card-custom">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <User className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.total}</p>
                <p className="text-xs text-[#78716c]">Teilnehmer</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-custom">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <FileCheck className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.statistik?.mit_bg || 0}</p>
                <p className="text-xs text-[#78716c]">Mit BG-Scan</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-custom">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <ClipboardCheck className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.statistik?.mit_erstgespraech || 0}</p>
                <p className="text-xs text-[#78716c]">Erstgespräch</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-custom">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <BookOpen className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.statistik?.mit_pruefung || 0}</p>
                <p className="text-xs text-[#78716c]">Geprüft</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-custom">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Award className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.statistik?.bestanden || 0}</p>
                <p className="text-xs text-[#78716c]">Bestanden</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search + Jahr-Filter */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Input
          placeholder="Suche nach Name, Kurs oder BG-Nummer..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
          data-testid="search-input"
        />
        <div className="flex items-center gap-1 rounded-lg border bg-white p-1" data-testid="jahr-filter">
          <span className="text-xs text-stone-500 px-2">Jahr (Start o. Ende):</span>
          {["all", "2025", "2026", "2027"].map((j) => (
            <button
              key={j}
              type="button"
              onClick={() => setJahrFilter(j)}
              data-testid={`jahr-filter-${j}`}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                jahrFilter === j
                  ? "bg-[#4A463D] text-white"
                  : "text-stone-700 hover:bg-stone-100"
              }`}
            >
              {j === "all" ? "Alle" : j}
            </button>
          ))}
        </div>
      </div>

      {/* Teilnehmer Tabelle */}
      <Card className="card-custom">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileCheck size={20} />
            BG-Teilnehmer für AZAV ({filteredTeilnehmer.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="animate-spin text-[#b8a99a]" size={32} />
            </div>
          ) : filteredTeilnehmer.length === 0 ? (
            <div className="text-center py-8 text-[#78716c]">
              <AlertCircle size={48} className="mx-auto mb-2 opacity-50" />
              <p>Keine AZAV-Teilnehmer gefunden</p>
              <p className="text-sm mt-2">Teilnehmer mit Bildungsgutschein und Kursstart 2025/2026 werden hier angezeigt</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Kurs</TableHead>
                    <TableHead>Kursstart</TableHead>
                    <TableHead>Kursende</TableHead>
                    <TableHead className="text-center">BG</TableHead>
                    <TableHead className="text-center">EG</TableHead>
                    <TableHead className="text-center">DS</TableHead>
                    <TableHead className="text-center">Vertrag</TableHead>
                    <TableHead className="text-center">Prüf.</TableHead>
                    <TableHead className="text-center">Best.</TableHead>
                    <TableHead className="text-center">Fortschritt</TableHead>
                    <TableHead className="text-center">Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTeilnehmer.map((t, idx) => {
                    const progress = getAzavProgress(t);
                    const kursInfo = getKursInfo(t.kurs);
                    
                    return (
                      <TableRow 
                        key={t.id || idx} 
                        data-testid={`teilnehmer-row-${idx}`}
                        className="cursor-pointer hover:bg-[#faf5f0] transition-colors"
                        onClick={() => handleTeilnehmerClick(t.id)}
                      >
                        <TableCell className="font-medium text-[#78716c]">{idx + 1}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{t.nachname}, {t.vorname}</p>
                            <p className="text-xs text-gray-500">{formatDate(t.geburtsdatum)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <Badge variant="outline" className="bg-blue-50">
                              {t.kurs || "-"}
                            </Badge>
                            {kursInfo && (
                              <p className="text-xs text-gray-500 mt-1">
                                {kursInfo.ue} UE {kursInfo.praktikum && `+ ${kursInfo.praktikum_wochen} Wo. Praktikum`}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(t.kurs_start)}</TableCell>
                        <TableCell>{formatDate(t.kurs_ende)}</TableCell>
                        <TableCell className="text-center">
                          <StatusIcon ok={t.azav_status?.hat_bg || t.bildungsgutschein_vorhanden} title="Bildungsgutschein-Scan" />
                        </TableCell>
                        <TableCell className="text-center">
                          <StatusIcon ok={t.azav_status?.hat_erstgespraech || t.erstgespraech_datum} title="Erstgespräch geführt" />
                        </TableCell>
                        <TableCell className="text-center">
                          <StatusIcon ok={t.azav_status?.hat_datenschutz || t.datenschutz_vorhanden} title="Datenschutz-Einverständnis" />
                        </TableCell>
                        <TableCell className="text-center">
                          <StatusIcon ok={t.vertrag || t.azav_status?.hat_vertrag} title="Maßnahmevertrag vorhanden" />
                        </TableCell>
                        <TableCell className="text-center">
                          <StatusIcon ok={t.azav_status?.hat_pruefung || t.pruefung_am} title="Prüfung abgelegt" />
                        </TableCell>
                        <TableCell className="text-center">
                          <StatusIcon ok={t.azav_status?.bestanden || t.bestanden} title="Prüfung bestanden" />
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${
                                  progress.percent === 100 ? 'bg-green-500' : 
                                  progress.percent >= 60 ? 'bg-amber-500' : 'bg-red-400'
                                }`}
                                style={{ width: `${progress.percent}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium">{progress.done}/{progress.total}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTeilnehmerClick(t.id);
                            }}
                          >
                            <Eye size={16} className="mr-1" />
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legende */}
      <div className="mt-4 p-4 bg-[#f8f7f5] rounded-lg">
        <p className="text-sm font-medium mb-2">Legende (AZAV-Pflichtdokumentation):</p>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm text-[#78716c]">
          <span className="flex items-center gap-2">
            <FileText size={16} className="text-blue-500" />
            <span><strong>BG</strong> = Bildungsgutschein</span>
          </span>
          <span className="flex items-center gap-2">
            <MessageSquare size={16} className="text-purple-500" />
            <span><strong>EG</strong> = Erstgespräch</span>
          </span>
          <span className="flex items-center gap-2">
            <Shield size={16} className="text-indigo-500" />
            <span><strong>DS</strong> = Datenschutz</span>
          </span>
          <span className="flex items-center gap-2">
            <FileCheck size={16} className="text-green-500" />
            <span><strong>Vertrag</strong> = Maßnahmevertrag</span>
          </span>
          <span className="flex items-center gap-2">
            <ClipboardCheck size={16} className="text-amber-500" />
            <span><strong>Prüf.</strong> = Prüfung abgelegt</span>
          </span>
          <span className="flex items-center gap-2">
            <Award size={16} className="text-emerald-500" />
            <span><strong>Best.</strong> = Prüfung bestanden</span>
          </span>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Klicken Sie auf einen Teilnehmer um Dokumente hochzuladen, das Erstgespräch zu dokumentieren und weitere AZAV-Pflichtfelder auszufüllen.
        </p>
      </div>

      </>)}

      {/* TeilnehmerDetail Dialog */}
      <TeilnehmerDetail
        teilnehmerId={selectedTeilnehmerId}
        isOpen={detailOpen}
        onClose={handleDetailClose}
        onUpdate={fetchData}
      />

      {/* ===== QM-Dokumentenliste Modal ===== */}
      <QMDokumente open={qmDokumenteOpen} onOpenChange={setQmDokumenteOpen} />

      {/* ===== Anhang-Reparatur Modal ===== */}
      <AnhangRepair open={anhangRepairOpen} onOpenChange={setAnhangRepairOpen} />

      {/* ===== AZAV Self-Audit Modal ===== */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="self-audit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield size={20} className="text-amber-600" />
              AZAV Self-Audit
            </DialogTitle>
            <DialogDescription>
              Prüft systematisch Datenkonsistenz für AZAV-Audit-Vorbereitung
            </DialogDescription>
          </DialogHeader>

          {/* Erklärung der Filter & Checks — eingeblendet wenn noch kein Report */}
          {!auditReport && (
            <details className="mb-2 text-xs bg-blue-50 border border-blue-200 rounded p-3">
              <summary className="cursor-pointer font-medium text-blue-900">ℹ Was prüft das Self-Audit?</summary>
              <div className="mt-2 space-y-1 text-blue-900">
                <p><b>Filter (optional):</b> Kursname wird unscharf gesucht (z. B. „LG1 und LG2" findet auch „Pflegeassistent LG1 & LG2"). Leer lassen = alle TN.</p>
                <p><b>6 Checks pro Run:</b></p>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li><b>Doppelte Dokumente:</b> TN mit ≥ 2 Erstgesprächen / Maßnahmeverträgen etc.</li>
                  <li><b>Unterrichtszeiten:</b> LG1/LG2 muss 09–15:30, sonst 09–15:00 Uhr</li>
                  <li><b>Fehlende Signaturen:</b> Sign-Requests „signiert" aber kein PNG eingebrannt</li>
                  <li><b>Anwesenheitsquote:</b> TN unter 70 % (AZAV-Mindestquote)</li>
                  <li><b>Klassenbuch:</b> Tage gegen Soll-Vorlage</li>
                  <li><b>Stundenplan-Match:</b> Klassenbuch-Themen vs. Vorlage (Fuzzy-Vergleich — kleine Wortlaut-Abweichungen werden ignoriert)</li>
                </ul>
                <p className="mt-1"><b>Ampel:</b> grün = 0 Auffälligkeiten · gelb = &lt; 20 · rot = ≥ 20</p>
              </div>
            </details>
          )}

          {/* Filter */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 p-3 bg-[#faf9f7] rounded-lg">
            <div>
              <label className="text-xs font-medium text-[#78716c]">Jahr</label>
              <Select value={auditFilter.jahr || "alle"} onValueChange={(v) => setAuditFilter({ ...auditFilter, jahr: v === "alle" ? "" : v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="audit-filter-jahr">
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle Jahre</SelectItem>
                  <SelectItem value="2026">2026 (nur Audit-Jahr)</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#78716c]">Kurs</label>
              <Select value={auditFilter.kurs || "alle"} onValueChange={(v) => setAuditFilter({ ...auditFilter, kurs: v === "alle" ? "" : v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="audit-filter-kurs">
                  <SelectValue placeholder="Alle Kurse" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="alle">Alle Kurse</SelectItem>
                  {availableKurse.map((k) => (
                    <SelectItem key={k} value={k}>{k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#78716c]">Finanzierung</label>
              <Select value={auditFilter.bg_sz || "alle"} onValueChange={(v) => setAuditFilter({ ...auditFilter, bg_sz: v === "alle" ? "" : v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="audit-filter-bgsz">
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle</SelectItem>
                  <SelectItem value="BG">BG (Bildungsgutschein)</SelectItem>
                  <SelectItem value="SZ">SZ (Selbstzahler)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#78716c]">Prüfung</label>
              <Select value={auditFilter.bestanden || "alle"} onValueChange={(v) => setAuditFilter({ ...auditFilter, bestanden: v === "alle" ? "" : v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="audit-filter-bestanden">
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle</SelectItem>
                  <SelectItem value="true">Bestanden</SelectItem>
                  <SelectItem value="false">Nicht bestanden</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#78716c]">Abbruch</label>
              <Select value={auditFilter.abgebrochen || "alle"} onValueChange={(v) => setAuditFilter({ ...auditFilter, abgebrochen: v === "alle" ? "" : v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="audit-filter-abbruch">
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle</SelectItem>
                  <SelectItem value="false">Nicht abgebrochen</SelectItem>
                  <SelectItem value="true">Abgebrochen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <Button
              onClick={runSelfAudit}
              disabled={auditLoading}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="run-audit-btn"
            >
              {auditLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Shield size={16} className="mr-2" />}
              Audit ausführen
            </Button>
            {auditReport && (
              <>
                <Button 
                  onClick={downloadAuditPdf} 
                  data-testid="download-audit-pdf-btn"
                  className="bg-[#4A463D] hover:bg-[#3A362D] text-white font-bold"
                  title="Professioneller AZAV-Selbstaudit-Bericht als PDF — vorzeigbar beim externen Audit"
                >
                  <FileText size={16} className="mr-2" />
                  AZAV-Bericht (PDF)
                </Button>
                <Button variant="outline" onClick={downloadAuditExcel} data-testid="download-audit-excel-btn"
                  className="border-green-500 text-green-700 hover:bg-green-50">
                  <Download size={16} className="mr-2" />
                  Excel-Export
                </Button>
                <Button variant="outline" onClick={downloadAuditJson} data-testid="download-audit-json-btn"
                  className="border-gray-300 text-gray-600 hover:bg-gray-50"
                  title="Nur für technische Auswertung — Rohdaten">
                  <Download size={16} className="mr-2" />
                  JSON (Rohdaten)
                </Button>
              </>
            )}
          </div>

          {/* Report */}
          {auditReport && (
            <div className="space-y-3" data-testid="audit-report">
              <div className={`p-3 rounded-lg border-2 ${
                auditReport.ampel === "gruen" ? "bg-green-50 border-green-300" :
                auditReport.ampel === "gelb" ? "bg-amber-50 border-amber-300" :
                "bg-red-50 border-red-300"
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-lg">
                      {auditReport.ampel === "gruen" ? "Alles im grünen Bereich" :
                       auditReport.ampel === "gelb" ? "Auffälligkeiten gefunden" :
                       "Sofortiger Handlungsbedarf"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {auditReport.teilnehmer_total} TN geprüft · {auditReport.issues_total} Auffälligkeit(en)
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {new Date(auditReport.generiert_am).toLocaleString("de-DE")}
                  </Badge>
                </div>
              </div>

              {/* Duplikate-Cleanup-Hinweis */}
              {(auditReport.issues?.doppelte_dokumente?.count || 0) > 0 && (
                <div className="p-3 rounded-lg border border-orange-300 bg-orange-50 flex items-center justify-between flex-wrap gap-2">
                  <div className="text-sm">
                    <strong className="text-orange-900">
                      {auditReport.issues.doppelte_dokumente.count} Teilnehmer
                    </strong>{" "}
                    <span className="text-orange-800">
                      haben mehrere Dateien gleicher Kategorie. Cleanup behält die neueste, löscht ältere (soft-delete, rückgängig machbar).
                    </span>
                  </div>
                  <Button
                    size="sm"
                    onClick={cleanupDuplicates}
                    disabled={cleanupDupLoading}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="cleanup-duplicates-btn"
                  >
                    {cleanupDupLoading ? (
                      <><Loader2 size={14} className="mr-1 animate-spin" /> Räume auf…</>
                    ) : (
                      <>Duplikate aufräumen</>
                    )}
                  </Button>
                </div>
              )}

              {Object.entries(auditReport.issues || {}).map(([key, val]) => (
                <details key={key} className="bg-white border rounded-lg p-3" open={val.count > 0}>
                  <summary className="cursor-pointer flex items-center justify-between">
                    <span className="font-medium capitalize">{key.replace(/_/g, " ")}</span>
                    <Badge variant={val.count === 0 ? "secondary" : "destructive"}>
                      {val.count}
                    </Badge>
                  </summary>
                  {val.items && val.items.length > 0 && (
                    <div className="mt-3 max-h-64 overflow-y-auto text-xs">
                      <pre className="bg-gray-50 p-2 rounded">{JSON.stringify(val.items, null, 2)}</pre>
                    </div>
                  )}
                </details>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Bulk-Resend Erstgespräch Modal ===== */}

      {/* ====== Sammelversand „Fehlende Dokumente" ====== */}
      <Dialog open={missingDocsOpen} onOpenChange={setMissingDocsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="missing-docs-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={20} className="text-amber-700" />
              Fehlende Dokumente sammelversenden
            </DialogTitle>
            <DialogDescription>
              Sucht bei allen BG-Teilnehmern, welche Pflicht-Dokumente (Maßnahmevertrag,
              Datenschutz, Erstgespräch) noch nicht signiert vorliegen, und versendet sie
              gesammelt. <strong>Das Dokumenten-Datum wird automatisch auf kurs_start − 1 Tag
              gesetzt</strong> — damit ist alles vor Kursbeginn unterschrieben.
            </DialogDescription>
          </DialogHeader>

          {/* Filter & Optionen */}
          <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-[#faf9f7] rounded-lg">
            <div>
              <label className="text-xs font-medium text-[#78716c]">Versand-Methode</label>
              <Select value={missingDocsMethode} onValueChange={setMissingDocsMethode}>
                <SelectTrigger className="h-9 text-sm" data-testid="missing-docs-methode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp (Liste mit Links)</SelectItem>
                  <SelectItem value="email">E-Mail (auto-versenden)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#78716c]">Nur Kursstart ab</label>
              <Input
                type="date"
                value={missingDocsMinDate}
                onChange={(e) => setMissingDocsMinDate(e.target.value)}
                className="h-9 text-sm"
                data-testid="missing-docs-min-date"
              />
              <p className="text-[10px] text-[#a8a29e] mt-0.5">2024er-Kurse standardmäßig ausgeblendet</p>
            </div>
            <div>
              <label className="text-xs font-medium text-[#78716c] block mb-1">Welche Dokumente?</label>
              <div className="flex flex-col gap-1 text-xs">
                {[
                  { k: "massnahmevertrag", label: "Maßnahmevertrag" },
                  { k: "datenschutz", label: "Datenschutz" },
                  { k: "erstgespraech", label: "Erstgespräch" },
                ].map(({ k, label }) => (
                  <label key={k} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!missingDocsTypes[k]}
                      onChange={(e) => setMissingDocsTypes({ ...missingDocsTypes, [k]: e.target.checked })}
                      data-testid={`missing-docs-checkbox-${k}`}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Vorschau-Button */}
          {!missingDocsPreview && !missingDocsResult && (
            <Button
              onClick={runMissingDocsPreview}
              disabled={missingDocsLoading}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="missing-docs-preview-btn"
            >
              {missingDocsLoading ? (
                <><Loader2 size={16} className="mr-2 animate-spin" /> Lade Vorschau...</>
              ) : (
                <>Vorschau anzeigen (Dry-Run)</>
              )}
            </Button>
          )}

          {/* Preview-Liste */}
          {missingDocsPreview && !missingDocsResult && (
            <div className="space-y-3">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm">
                <strong>{missingDocsPreview.total_betroffen}</strong> Teilnehmer mit fehlenden Dokumenten gefunden.
              </div>
              {missingDocsPreview.total_betroffen > 0 && (
                <div className="border rounded max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#4A463D] text-white sticky top-0">
                      <tr>
                        <th className="text-left p-2">Teilnehmer</th>
                        <th className="text-left p-2">Kurs</th>
                        <th className="text-left p-2">Fehlende Dokumente</th>
                        <th className="text-left p-2">Dok-Datum</th>
                        <th className="text-left p-2">Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(missingDocsPreview.results || []).map((r, idx) => (
                        <tr key={r.teilnehmer_id || idx} className="border-t hover:bg-amber-50">
                          <td className="p-2 font-medium">{r.name}</td>
                          <td className="p-2 text-[#78716c]">{r.kurs || "—"}</td>
                          <td className="p-2">{(r.fehlende_dokumente || []).join(", ")}</td>
                          <td className="p-2 font-mono">{r.dokument_datum || "—"}</td>
                          <td className="p-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-100"
                              onClick={() => sendMissingDocsForSingleTn(r.teilnehmer_id, r.name)}
                              disabled={!!singleSendLoading[r.teilnehmer_id]}
                              data-testid={`missing-docs-single-send-${idx}`}
                            >
                              {singleSendLoading[r.teilnehmer_id] ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : missingDocsMethode === "whatsapp" ? (
                                <><Phone size={12} className="mr-1" /> Nur an diesen</>
                              ) : (
                                <><Mail size={12} className="mr-1" /> Nur an diesen</>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {missingDocsPreview.total_betroffen > 0 && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setMissingDocsPreview(null); }}
                    className="flex-1"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    onClick={runMissingDocsLive}
                    disabled={missingDocsLoading}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="missing-docs-live-btn"
                  >
                    {missingDocsLoading ? (
                      <><Loader2 size={16} className="mr-2 animate-spin" /> Sende...</>
                    ) : (
                      <>Jetzt sammelversenden ({missingDocsPreview.total_betroffen})</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Live-Ergebnis mit WhatsApp-Links */}
          {missingDocsResult && (
            <div className="space-y-3">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm">
                Sammelversand abgeschlossen — <strong>{(missingDocsResult.results || []).filter(r => ["whatsapp_link_ready","email_sent"].includes(r.status)).length}</strong> von {missingDocsResult.total_betroffen} erfolgreich.
              </div>
              <div className="border rounded max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#4A463D] text-white sticky top-0">
                    <tr>
                      <th className="text-left p-2">Teilnehmer</th>
                      <th className="text-left p-2">Fehlende Doks</th>
                      <th className="text-left p-2">Status / Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(missingDocsResult.results || []).map((r, idx) => (
                      <tr key={idx} className="border-t hover:bg-amber-50">
                        <td className="p-2 font-medium">{r.name}</td>
                        <td className="p-2">{(r.fehlende_dokumente || []).join(", ")}</td>
                        <td className="p-2">
                          {r.whatsapp_url ? (
                            <a
                              href={r.whatsapp_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded font-medium"
                              data-testid={`missing-docs-wa-link-${idx}`}
                            >
                              <Phone size={12} /> WhatsApp öffnen
                            </a>
                          ) : (
                            <span className={
                              r.status === "email_sent" ? "text-emerald-700 font-medium" :
                              r.status?.startsWith("error") || r.status === "no_phone" || r.status === "no_email" ? "text-red-700" :
                              "text-[#78716c]"
                            }>
                              {r.status}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button
                variant="outline"
                onClick={() => { setMissingDocsOpen(false); setMissingDocsPreview(null); setMissingDocsResult(null); fetchData(); }}
                className="w-full"
              >
                Schließen
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>


      <Dialog open={resendOpen} onOpenChange={setResendOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-resend-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={20} className="text-blue-600" />
              Erstgespräche neu senden (Bulk)
            </DialogTitle>
            <DialogDescription>
              Findet alle Teilnehmer, deren Erstgespräch ohne gespeicherte PNG-Signatur abgeschlossen wurde,
              und erstellt neue Sign-Requests. Beim erneuten Unterschreiben wird das alte EG-PDF automatisch ersetzt.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-[#faf9f7] rounded-lg">
            <div>
              <label className="text-xs font-medium text-[#78716c]">Modus</label>
              <Select value={resendDryRun ? "dry" : "live"} onValueChange={(v) => setResendDryRun(v === "dry")}>
                <SelectTrigger className="h-9 text-sm" data-testid="resend-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dry">Dry-Run (nur Liste anzeigen)</SelectItem>
                  <SelectItem value="live">Live (neue Requests erzeugen)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#78716c]">Standard-Versand</label>
              <Select value={resendMethode} onValueChange={setResendMethode}>
                <SelectTrigger className="h-9 text-sm" data-testid="resend-methode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp (Link kopieren)</SelectItem>
                  <SelectItem value="email">E-Mail (auto-versenden)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <Button
              onClick={runBulkResend}
              disabled={resendLoading}
              className={resendDryRun ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
              data-testid="run-resend-btn"
            >
              {resendLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Mail size={16} className="mr-2" />}
              {resendDryRun ? "Kandidaten anzeigen" : "JETZT NEU SENDEN"}
            </Button>
          </div>

          {resendResult && (
            <div className="space-y-3" data-testid="resend-result">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-bold">{resendResult.total_candidates} Kandidat(en)</p>
                <p className="text-sm text-gray-600">
                  {resendResult.dry_run ? "Dry-Run – keine Änderungen vorgenommen" : "Neue Sign-Requests erstellt"}
                </p>
              </div>

              {resendResult.results && resendResult.results.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Kurs</TableHead>
                      <TableHead>Versand</TableHead>
                      <TableHead>Status / Link</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resendResult.results.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm font-medium">{r.tn_name || r.teilnehmer_id?.slice(0, 8)}</TableCell>
                        <TableCell className="text-xs text-gray-600">{r.kurs || "—"}</TableCell>
                        <TableCell className="text-xs">{r.versand_methode}</TableCell>
                        <TableCell className="text-xs">
                          {r.whatsapp_url ? (
                            <a href={r.whatsapp_url} target="_blank" rel="noreferrer" className="text-green-700 underline">
                              <Phone size={12} className="inline mr-1" />WhatsApp öffnen
                            </a>
                          ) : (
                            <Badge variant="outline">{r.status}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* ===== Kurs-Cleanup Modal ===== */}
      <Dialog open={kursCleanupOpen} onOpenChange={setKursCleanupOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="kurs-cleanup-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen size={20} className="text-purple-600" />
              Kurse aufräumen — Duplikate &amp; Schreibvarianten zusammenführen
            </DialogTitle>
            <DialogDescription>
              Übersicht aller Kurs-Namen mit TN-Anzahl. Doppelte Schreibweisen können in den
              <b> kanonischen Namen</b> umbenannt werden — alle TN werden automatisch verschoben.
            </DialogDescription>
          </DialogHeader>

          {kursCleanupLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={28} className="animate-spin text-purple-600" />
            </div>
          ) : kursUebersicht ? (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 bg-purple-50 border border-purple-200 rounded text-center">
                  <p className="text-2xl font-bold text-purple-900">{kursUebersicht.verwendete_kurse_total}</p>
                  <p className="text-xs text-purple-700">Verwendete Kurs-Namen</p>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-center">
                  <p className="text-2xl font-bold text-amber-900">{kursUebersicht.duplikate_count}</p>
                  <p className="text-xs text-amber-700">Duplikate / Schreibvarianten</p>
                </div>
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-center">
                  <p className="text-2xl font-bold text-emerald-900">{kursUebersicht.kanonische_kurse.length}</p>
                  <p className="text-xs text-emerald-700">Kanonische Kurse (Soll)</p>
                </div>
                <div className="p-3 bg-gray-100 border border-gray-200 rounded text-center">
                  <p className="text-2xl font-bold text-gray-700">{kursUebersicht.legacy_count || 0}</p>
                  <p className="text-xs text-gray-600">Ninox-Legacy (Zahlen)</p>
                </div>
              </div>

              <div className="flex items-center gap-2 py-2">
                <input
                  type="checkbox"
                  id="hide-legacy"
                  checked={hideLegacy}
                  onChange={(e) => setHideLegacy(e.target.checked)}
                  className="cursor-pointer"
                  data-testid="hide-legacy-toggle"
                />
                <label htmlFor="hide-legacy" className="text-sm cursor-pointer">
                  Ninox-Legacy-Einträge ausblenden (reine Zahlen / sehr kurze Namen)
                </label>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kurs-Name</TableHead>
                    <TableHead className="w-24 text-center">TN&nbsp;Haupt</TableHead>
                    <TableHead className="w-24 text-center">TN&nbsp;Array</TableHead>
                    <TableHead>Status / Ziel</TableHead>
                    <TableHead className="w-32"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kursUebersicht.eintraege
                    .filter(e => !(hideLegacy && e.is_legacy))
                    .map((e) => {
                    const targetName = mergeOverride[e.kurs_name] || e.vorschlag_kanonisch || "";
                    const canMerge = !e.ist_kanonisch && targetName && targetName !== e.kurs_name;
                    return (
                      <TableRow key={e.kurs_name} className={e.is_legacy ? "opacity-60" : ""}>
                        <TableCell className="text-sm">{e.kurs_name}</TableCell>
                        <TableCell className="text-center text-sm">{e.anzahl_tn_hauptkurs}</TableCell>
                        <TableCell className="text-center text-sm">{e.anzahl_kurse_array}</TableCell>
                        <TableCell>
                          {e.ist_kanonisch ? (
                            <Badge className="bg-emerald-100 text-emerald-700">✓ Kanonisch</Badge>
                          ) : (
                            <Select
                              value={targetName || "_select_"}
                              onValueChange={(v) => setMergeOverride({ ...mergeOverride, [e.kurs_name]: v === "_select_" ? "" : v })}
                            >
                              <SelectTrigger className="h-8 text-xs" data-testid={`merge-target-${e.kurs_name}`}>
                                <SelectValue placeholder="Ziel wählen…" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_select_">— Ziel wählen —</SelectItem>
                                {kursUebersicht.kanonische_kurse.map((k) => (
                                  <SelectItem key={k} value={k}>{k}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell>
                          {canMerge && (
                            <Button
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                              onClick={() => startMergePreview(e.kurs_name, targetName)}
                              data-testid={`merge-btn-${e.kurs_name}`}
                            >
                              Vorschau
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <div className="text-xs text-gray-500 pt-2 border-t">
                <p><b>Hinweis:</b> „TN Haupt" = TN.kurs (Hauptkurs). „TN Array" = Einträge im kurse[]-Array.</p>
                <p>Beim Mergen werden Einträge aus beiden Quellen auf den Ziel-Namen umbenannt — keine TN-Daten gehen verloren.</p>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ===== Kurs-Merge Vorschau-Dialog ===== */}
      <Dialog open={!!mergePreview} onOpenChange={(o) => !o && setMergePreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="merge-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertCircle size={20} />
              Bestätigung Kurs-Merge — Sicherheits-Vorschau
            </DialogTitle>
            <DialogDescription>
              <span className="font-mono text-xs">{mergePreview?.von}</span>
              {" → "}
              <span className="font-mono text-xs text-emerald-700 font-bold">{mergePreview?.nach}</span>
            </DialogDescription>
          </DialogHeader>

          {mergePreview && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-2xl font-bold text-amber-900">{mergePreview.total_tn}</p>
                  <p className="text-xs text-amber-700">Betroffene Teilnehmer insgesamt</p>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-xs font-medium text-blue-900 mb-1">Aufteilung nach Jahr:</p>
                  <div className="text-sm text-blue-900">
                    {Object.entries(mergePreview.jahr_counts || {}).sort().reverse().map(([j, c]) => (
                      <span key={j} className="inline-block mr-3">
                        <b>{j}:</b> {c}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {(mergePreview.jahr_counts?.["2026"] || 0) > 0 && (
                <div className="mb-3 p-3 bg-red-50 border-2 border-red-300 rounded">
                  <p className="text-sm font-bold text-red-900">
                    ACHTUNG: {mergePreview.jahr_counts["2026"]} aktive 2026er TN sind betroffen.
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    Prüfe in der Liste unten, ob das wirklich so gewollt ist — eine versehentliche
                    Umbenennung der laufenden Kurse hat AZAV-Folgen.
                  </p>
                </div>
              )}

              <p className="text-sm font-medium mb-2">Betroffene Teilnehmer:</p>
              <div className="max-h-72 overflow-y-auto border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-28">Kurs-Start</TableHead>
                      <TableHead className="w-20">Status</TableHead>
                      <TableHead className="w-32">Wo?</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mergePreview.affected_tn.map((t) => (
                      <TableRow key={t.teilnehmer_id} className={t.jahr === "2026" ? "bg-red-50" : ""}>
                        <TableCell className="text-sm">{t.name}</TableCell>
                        <TableCell className="text-xs">{t.kurs_start || "—"}</TableCell>
                        <TableCell className="text-xs">{t.status || "—"}</TableCell>
                        <TableCell className="text-xs">
                          {t.in_hauptkurs && <Badge className="bg-purple-100 text-purple-700 mr-1">Haupt</Badge>}
                          {t.in_array && <Badge className="bg-blue-100 text-blue-700">Array</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <DialogFooter className="mt-4 gap-2">
                <Button variant="outline" onClick={() => setMergePreview(null)} data-testid="merge-cancel-btn">
                  Abbrechen
                </Button>
                <Button
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={confirmMerge}
                  data-testid="merge-confirm-btn"
                >
                  Jetzt mergen ({mergePreview.total_tn} TN)
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Wochen-Backup-E-Mail Modal ===== */}
      <Dialog open={backupEmailsOpen} onOpenChange={setBackupEmailsOpen}>
        <DialogContent className="max-w-2xl" data-testid="backup-email-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={20} className="text-emerald-600" />
              Automatisches Wochen-Backup per E-Mail
            </DialogTitle>
            <DialogDescription>
              Jeden Sonntag zwischen 04:00 und 05:00 Uhr (MEZ) wird ein ZIP mit allen Anhängen
              an die hinterlegten E-Mail-Adressen versendet. Bei &gt; 24 MB bekommst du nur einen
              Hinweis-Link statt der Datei (E-Mail-Größenlimit).
            </DialogDescription>
          </DialogHeader>
          
          {backupConfig && (
            <div className="space-y-4">
              {/* Status */}
              <div className={`p-3 rounded-lg border ${
                backupConfig.enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
              }`}>
                <p className="font-medium">
                  {backupConfig.enabled ? 'Automatisches Backup aktiv' : 'Noch keine Empfänger konfiguriert'}
                </p>
                <p className="text-xs text-gray-600 mt-1">{backupConfig.schedule}</p>
                {backupConfig.last_run_at && (
                  <p className="text-xs text-gray-600 mt-1">
                    Letzter Lauf: {new Date(backupConfig.last_run_at).toLocaleString('de-DE')}
                  </p>
                )}
              </div>
              
              {/* Empfänger-Liste */}
              <div>
                <p className="font-semibold mb-2">Empfänger</p>
                <div className="space-y-2">
                  {backupConfig.emails.map((e, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{e.email}</p>
                        {e.name && <p className="text-xs text-gray-500">{e.name}</p>}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendBackupNow(e.email, e.name)}
                        data-testid={`backup-test-send-${idx}`}
                        title="Backup jetzt sofort an diese Adresse schicken"
                      >
                        Jetzt testen
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => {
                          const newEmails = backupConfig.emails.filter((_, i) => i !== idx);
                          saveBackupConfig(newEmails);
                        }}
                        data-testid={`backup-remove-${idx}`}
                      >
                        Entfernen
                      </Button>
                    </div>
                  ))}
                  {backupConfig.emails.length === 0 && (
                    <p className="text-sm text-gray-500 italic">Noch keine Empfänger</p>
                  )}
                </div>
              </div>
              
              {/* Neuer Empfänger */}
              <div className="border-t pt-3">
                <p className="font-semibold mb-2">Empfänger hinzufügen</p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="email@firma.de"
                    value={newBackupEmail}
                    onChange={(e) => setNewBackupEmail(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded text-sm"
                    data-testid="new-backup-email-input"
                  />
                  <input
                    type="text"
                    placeholder="Name (optional)"
                    value={newBackupName}
                    onChange={(e) => setNewBackupName(e.target.value)}
                    className="w-40 px-3 py-2 border rounded text-sm"
                    data-testid="new-backup-name-input"
                  />
                  <Button
                    onClick={() => {
                      if (!newBackupEmail || !newBackupEmail.includes("@")) {
                        toast.error("Bitte gültige E-Mail eingeben");
                        return;
                      }
                      const newEmails = [...backupConfig.emails, { email: newBackupEmail, name: newBackupName }];
                      saveBackupConfig(newEmails);
                      setNewBackupEmail("");
                      setNewBackupName("");
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="add-backup-email-btn"
                  >
                    Hinzufügen
                  </Button>
                </div>
              </div>
              
              <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded">
                <strong>Tipp:</strong> Trage z. B. deine eigene E-Mail und die deines Geschäftsführers ein.
                Speichere die ZIP wöchentlich auf der Diskstation — so hast du ein Offline-Backup deiner Daten.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Synology WebDAV Backup Modal ===== */}
      <Dialog open={synologyOpen} onOpenChange={setSynologyOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="synology-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database size={20} className="text-sky-600" />
              Synology Diskstation — Direkter Backup-Push
            </DialogTitle>
            <DialogDescription>
              Lädt das ZIP-Backup automatisch jeden Sonntag per WebDAV direkt auf deine eigene Synology.
              Unabhängig von E-Mail-Größenlimits und Drittanbietern.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Status-Box */}
            {synologyConfig?.configured && (
              <div className={`p-3 rounded border text-sm ${synologyConfig.enabled ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                <div className="font-medium">
                  {synologyConfig.enabled ? 'Synology-Backup aktiv' : 'Konfiguriert, aber deaktiviert'}
                </div>
                {synologyConfig.last_run_at && (
                  <div className="text-xs mt-1">
                    Letzter Lauf: {new Date(synologyConfig.last_run_at).toLocaleString('de-DE')} —
                    Status: {synologyConfig.last_status === 'ok' ? 'OK' : `${synologyConfig.last_error || 'Fehler'}`}
                    {synologyConfig.last_bytes && ` (${(synologyConfig.last_bytes / 1024 / 1024).toFixed(1)} MB)`}
                  </div>
                )}
              </div>
            )}

            {/* Setup-Anleitung */}
            <details className="bg-sky-50 border border-sky-200 rounded p-3 text-sm">
              <summary className="cursor-pointer font-medium text-sky-900">
                Anleitung: Synology vorbereiten (einmalig, ~5 Min)
              </summary>
              <ol className="mt-2 ml-5 list-decimal space-y-1 text-sky-900">
                <li>DSM → <b>Systemsteuerung → Dateidienste → WebDAV</b> → „HTTPS aktivieren" (Port <b>5006</b>)</li>
                <li>DSM → <b>Systemsteuerung → Benutzer</b> → neuen Benutzer anlegen, z.B. <code className="bg-white px-1">crm_backup</code></li>
                <li>DSM → <b>File Station</b> → Ordner anlegen, z.B. <code className="bg-white px-1">/CRM-Backup</code> — diesem Benutzer Schreibrecht geben</li>
                <li>Router: Port <b>5006</b> auf die Synology weiterleiten (oder QuickConnect/DDNS einrichten)</li>
                <li>URL hier eintragen, z.B. <code className="bg-white px-1">https://deinname.synology.me:5006</code></li>
              </ol>
            </details>

            {/* Konfigurations-Formular */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Synology-URL (mit https und Port)
                </label>
                <Input
                  type="text"
                  placeholder="https://deinname.synology.me:5006"
                  value={synologyForm.base_url}
                  onChange={(e) => setSynologyForm({ ...synologyForm, base_url: e.target.value })}
                  data-testid="synology-url-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Benutzername</label>
                <Input
                  type="text"
                  placeholder="crm_backup"
                  value={synologyForm.username}
                  onChange={(e) => setSynologyForm({ ...synologyForm, username: e.target.value })}
                  data-testid="synology-user-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Passwort {synologyConfig?.has_password && !synologyForm.password && (
                    <span className="text-xs text-emerald-600">(gespeichert — leer lassen wenn unverändert)</span>
                  )}
                </label>
                <Input
                  type="password"
                  placeholder={synologyConfig?.has_password ? "••••••" : "Passwort"}
                  value={synologyForm.password}
                  onChange={(e) => setSynologyForm({ ...synologyForm, password: e.target.value })}
                  data-testid="synology-pass-input"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Ziel-Ordner</label>
                <Input
                  type="text"
                  placeholder="/CRM-Backup"
                  value={synologyForm.target_folder}
                  onChange={(e) => setSynologyForm({ ...synologyForm, target_folder: e.target.value })}
                  data-testid="synology-folder-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Backups behalten (Rotation)
                </label>
                <Input
                  type="number"
                  min="0"
                  max="52"
                  placeholder="4 (= ein Monat)"
                  value={synologyForm.max_keep}
                  onChange={(e) => setSynologyForm({ ...synologyForm, max_keep: parseInt(e.target.value) || 0 })}
                  data-testid="synology-keep-input"
                />
              </div>

              <div className="md:col-span-2 flex gap-4 items-center pt-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={synologyForm.enabled}
                    onChange={(e) => setSynologyForm({ ...synologyForm, enabled: e.target.checked })}
                    data-testid="synology-enabled-checkbox"
                  />
                  <b>Automatisches Wochen-Backup aktivieren</b>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={synologyForm.verify_tls}
                    onChange={(e) => setSynologyForm({ ...synologyForm, verify_tls: e.target.checked })}
                    data-testid="synology-tls-checkbox"
                  />
                  TLS-Zertifikat prüfen <span className="text-xs text-gray-500">(bei Self-Signed/DDNS abschalten)</span>
                </label>
              </div>
            </div>

            {/* Test-Ergebnis */}
            {synologyTestResult && (
              <div className={`p-3 rounded border text-sm ${synologyTestResult.ok ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-rose-50 border-rose-300 text-rose-900'}`}>
                <div className="font-medium">
                  {synologyTestResult.ok ? 'Verbindung OK' : 'Verbindung fehlgeschlagen'}
                </div>
                <div className="text-xs mt-1">{synologyTestResult.message || synologyTestResult.error}</div>
                {synologyTestResult.url && (
                  <div className="text-xs mt-1 font-mono">{synologyTestResult.url}</div>
                )}
              </div>
            )}

            {/* Push-Ergebnis */}
            {synologyPushResult && synologyPushResult.ok && (
              <div className="p-3 rounded border bg-emerald-50 border-emerald-300 text-emerald-900 text-sm">
                <div className="font-medium">Backup erfolgreich hochgeladen</div>
                <div className="text-xs mt-1">
                  Datei: <span className="font-mono">{synologyPushResult.filename}</span><br/>
                  Größe: {(synologyPushResult.bytes / 1024 / 1024).toFixed(2)} MB<br/>
                  {synologyPushResult.manifest && (
                    <>Dateien im ZIP: {synologyPushResult.manifest.files_total}<br/></>
                  )}
                  {synologyPushResult.cleanup?.deleted?.length > 0 && (
                    <>Alte Backups gelöscht: {synologyPushResult.cleanup.deleted.length}</>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={testSynologyConnection}
              disabled={synologyTesting || !synologyForm.base_url || !synologyForm.username}
              data-testid="synology-test-btn"
            >
              {synologyTesting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
              Verbindung testen
            </Button>
            <Button
              variant="outline"
              onClick={saveSynologyConfig}
              data-testid="synology-save-btn"
            >
              Speichern
            </Button>
            <Button
              className="bg-sky-600 hover:bg-sky-700 text-white"
              onClick={pushSynologyNow}
              disabled={synologyPushing || !synologyConfig?.configured || !synologyForm.enabled}
              data-testid="synology-push-now-btn"
              title={!synologyForm.enabled ? "Erst aktivieren und speichern" : "Backup sofort hochladen"}
            >
              {synologyPushing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
              Jetzt hochladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Kurse zuordnen Modal (Ninox-Importe) ===== */}
      <Dialog open={kursZuordnenOpen} onOpenChange={setKursZuordnenOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="kurs-zuordnen-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen size={20} className="text-indigo-600" />
              Kurse zuordnen — Teilnehmer ohne Kursname
            </DialogTitle>
            <DialogDescription>
              Wähle pro Teilnehmer den passenden Kurs aus dem Dropdown und klick „Zuordnen".
              Der Kurs wird sofort am Teilnehmer hinterlegt.
            </DialogDescription>
          </DialogHeader>

          {/* Filter */}
          <div className="flex gap-3 items-center bg-gray-50 p-3 rounded border border-gray-200 flex-wrap">
            <label className="text-sm flex items-center gap-2">
              <span>Quelle:</span>
              <Select
                value={ohneKursFilter.source || "alle"}
                onValueChange={(v) => setOhneKursFilter(f => ({ ...f, source: v === "alle" ? "" : v }))}
              >
                <SelectTrigger className="w-44" data-testid="ohne-kurs-source-select">
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle</SelectItem>
                  <SelectItem value="ninox">Nur Ninox-Importe</SelectItem>
                </SelectContent>
              </Select>
            </label>
            <label className="text-sm flex items-center gap-2">
              <input
                type="checkbox"
                checked={ohneKursFilter.nur_bg}
                onChange={(e) => setOhneKursFilter(f => ({ ...f, nur_bg: e.target.checked }))}
                data-testid="ohne-kurs-nurbg-checkbox"
              />
              Nur AZAV-relevante BG-TN
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={loadOhneKurs}
              disabled={ohneKursLoading}
              data-testid="ohne-kurs-reload-btn"
            >
              {ohneKursLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
              Neu laden
            </Button>
            <div className="ml-auto text-sm text-gray-600">
              <b>{ohneKursList.length}</b> Teilnehmer ohne Kurs
            </div>
          </div>

          {/* Tabelle */}
          {ohneKursLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={28} className="animate-spin text-indigo-600" />
            </div>
          ) : ohneKursList.length === 0 ? (
            <div className="text-center py-10 text-emerald-700 font-medium">
              Keine Teilnehmer ohne Kurszuordnung — alles eingepflegt!
            </div>
          ) : (
            <div className="max-h-[55vh] overflow-y-auto border rounded">
              <Table>
                <TableHeader className="sticky top-0 bg-gray-100 z-10">
                  <TableRow>
                    <TableHead className="w-[200px]">Name</TableHead>
                    <TableHead className="w-[100px]">Geb.</TableHead>
                    <TableHead className="w-[100px]">Kurs-Start</TableHead>
                    <TableHead className="w-[120px]">Finanzierung</TableHead>
                    <TableHead className="w-[80px]">Quelle</TableHead>
                    <TableHead>Kurs zuordnen</TableHead>
                    <TableHead className="w-[120px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ohneKursList.map((t) => (
                    <TableRow key={t.id} data-testid={`ohne-kurs-row-${t.id}`}>
                      <TableCell className="font-medium text-sm">
                        {(t.vorname || '') + ' ' + (t.nachname || '') || <span className="text-gray-400">(Kein Name)</span>}
                        {t.email && <div className="text-xs text-gray-500 truncate max-w-[180px]">{t.email}</div>}
                      </TableCell>
                      <TableCell className="text-xs">{t.geburtsdatum ? formatDate(t.geburtsdatum) : '—'}</TableCell>
                      <TableCell className="text-xs">{t.kurs_start ? formatDate(t.kurs_start) : '—'}</TableCell>
                      <TableCell className="text-xs">
                        {t.bildungsgutschein_vorhanden || t.bg_sz === 'BG' || t.finanzierungsart === 'bildungsgutschein' ? (
                          (t.azav_status?.hat_bg || t.bildungsgutschein_nummer) ? (
                            <Badge className="bg-stone-900 text-white border-0 text-xs" data-testid={`bg-bestaetigt-${t.id}`}>BG bestätigt</Badge>
                          ) : (
                            <Badge className="bg-[#FBEDE7] text-[#B4502F] border border-[#F0C9B8] text-xs" title="Nur laut Ninox/Eintrag – kein Bildungsgutschein-Dokument und keine BG-Nummer hinterlegt" data-testid={`bg-ohne-nachweis-${t.id}`}>BG ohne Nachweis</Badge>
                          )
                        ) : (
                          <span className="text-gray-500">{t.finanzierungsart || '—'}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {t.import_source === 'ninox' ? (
                          <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">Ninox</Badge>
                        ) : (
                          <span className="text-gray-500">{t.import_source || '—'}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={zuordnung[t.id] || ""}
                          onValueChange={(v) => setZuordnung(prev => ({ ...prev, [t.id]: v }))}
                        >
                          <SelectTrigger className="w-full" data-testid={`kurs-select-${t.id}`}>
                            <SelectValue placeholder="Kurs auswählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableKurse.map(k => (
                              <SelectItem key={k} value={k}>{k}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          onClick={() => zuordnenSingle(t.id, zuordnung[t.id])}
                          disabled={!zuordnung[t.id] || zuordnenLoading[t.id]}
                          data-testid={`zuordnen-btn-${t.id}`}
                        >
                          {zuordnenLoading[t.id] ? <Loader2 size={14} className="animate-spin" /> : "Zuordnen"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setKursZuordnenOpen(false)}
              data-testid="kurs-zuordnen-close-btn"
            >
              Schließen
            </Button>
            {Object.values(zuordnung).filter(Boolean).length > 0 && (
              <Button
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={zuordnenBulk}
                data-testid="kurs-zuordnen-bulk-btn"
              >
                {Object.values(zuordnung).filter(Boolean).length} Zuordnungen auf einmal speichern
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Storage-Diagnose Modal ===== */}
      <Dialog open={storageOpen} onOpenChange={setStorageOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="storage-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle size={20} className="text-rose-600" />
              MongoDB-Storage Diagnose &amp; Cleanup
            </DialogTitle>
            <DialogDescription>
              Atlas Free-Tier ist auf 512 MB begrenzt. Hier siehst du den aktuellen Verbrauch und kannst aufräumen.
            </DialogDescription>
          </DialogHeader>

          {storageLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={28} className="animate-spin text-rose-600" />
            </div>
          ) : storageStatus ? (
            <div className="space-y-4">
              {/* KPI-Kacheln */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className={`p-3 border-2 rounded ${
                  storageStatus.total_mb > 450 ? "bg-red-50 border-red-300" :
                  storageStatus.total_mb > 350 ? "bg-amber-50 border-amber-300" :
                  "bg-emerald-50 border-emerald-300"
                }`}>
                  <p className="text-2xl font-bold">{storageStatus.total_mb} MB</p>
                  <p className="text-xs">von 512 MB ({Math.round(storageStatus.total_mb / 512 * 100)} %)</p>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-2xl font-bold text-blue-900">{storageStatus.anhaenge_b64_mb} MB</p>
                  <p className="text-xs text-blue-700">PDFs als Base64 ({storageStatus.anhaenge_b64_count} Stk)</p>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-2xl font-bold text-amber-900">{storageStatus.sign_requests_signed_old_count}</p>
                  <p className="text-xs text-amber-700">Alte Sign-Requests &gt; 6 Mon</p>
                </div>
                <div className="p-3 bg-gray-100 border rounded">
                  <p className="text-2xl font-bold text-gray-700">{storageStatus.db_snapshots_count}</p>
                  <p className="text-xs text-gray-600">DB-Snapshots</p>
                </div>
              </div>

              {/* Cleanup-Actions */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="font-medium text-sm mb-2 text-amber-900">Aufräumen (Quick-Wins)</p>
                  <p className="text-xs text-amber-700 mb-3">
                    Duplikate, alte Sign-Requests (&gt; 6 Mon mit bereits eingebrannter Signatur),
                    DB-Snapshots (letzte 2 bleiben), E-Mail-Log &gt; 90 Tage.
                  </p>
                  <Button
                    onClick={runCleanupPreview}
                    className="bg-amber-600 hover:bg-amber-700 text-white w-full"
                    data-testid="cleanup-preview-btn"
                  >
                    Vorschau Cleanup
                  </Button>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="font-medium text-sm mb-2 text-blue-900">PDFs auslagern (Big-Win)</p>
                  <p className="text-xs text-blue-700 mb-3">
                    Verschiebt base64-Anhänge aus MongoDB ins Filesystem
                    (<code>/app/uploads/teilnehmer_anhaenge/</code>). Backend liest <code>file_path</code> als Fallback.
                  </p>
                  <Button
                    onClick={runMigratePreview}
                    className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                    data-testid="migrate-preview-btn"
                  >
                    Vorschau Migration
                  </Button>
                </div>
              </div>

              {/* Top Collections */}
              <details className="bg-white border rounded p-3" open>
                <summary className="cursor-pointer font-medium">Top Collections (Größe)</summary>
                <div className="mt-2 max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Collection</TableHead>
                        <TableHead className="text-right">Größe (MB)</TableHead>
                        <TableHead className="text-right">Docs</TableHead>
                        <TableHead className="text-right">Ø KB</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {storageStatus.collections_top.map((c) => (
                        <TableRow key={c.name}>
                          <TableCell className="text-sm">{c.name}</TableCell>
                          <TableCell className="text-right text-sm font-mono">{c.size_mb}</TableCell>
                          <TableCell className="text-right text-sm text-gray-600">{c.count.toLocaleString("de-DE")}</TableCell>
                          <TableCell className="text-right text-sm text-gray-600">{c.avg_kb}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </details>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Cleanup-Vorschau-Dialog */}
      <Dialog open={!!cleanupPreview} onOpenChange={(o) => !o && setCleanupPreview(null)}>
        <DialogContent className="max-w-xl" data-testid="cleanup-preview-modal">
          <DialogHeader>
            <DialogTitle>Cleanup-Vorschau (nichts gelöscht)</DialogTitle>
            <DialogDescription>Diese Einträge würden entfernt werden:</DialogDescription>
          </DialogHeader>
          {cleanupPreview && (
            <div className="space-y-2 text-sm">
              <div className="p-2 bg-amber-50 border rounded">
                <b>Doppelte Anhänge:</b> {cleanupPreview.details?.duplikate?.array_entfernt || 0} Array-Einträge + {cleanupPreview.details?.duplikate?.collection_entfernt || 0} Collection-Einträge
              </div>
              <div className="p-2 bg-amber-50 border rounded">
                <b>Alte signierte Sign-Requests:</b> {cleanupPreview.details?.alte_sign_requests?.entfernt || 0} Stück
              </div>
              <div className="p-2 bg-amber-50 border rounded">
                <b>DB-Snapshots (letzte 2 bleiben):</b> {cleanupPreview.details?.db_snapshots?.entfernt || 0} Stück
              </div>
              <div className="p-2 bg-amber-50 border rounded">
                <b>E-Mail-Log &gt; 90 Tage:</b> {cleanupPreview.details?.email_log?.entfernt || 0} Stück
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCleanupPreview(null)}>Abbrechen</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={confirmCleanup}
              disabled={cleanupRunning}
              data-testid="cleanup-confirm-btn"
            >
              {cleanupRunning ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Jetzt aufräumen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Migrate-Vorschau-Dialog */}
      <Dialog open={!!migratePreview} onOpenChange={(o) => !o && setMigratePreview(null)}>
        <DialogContent className="max-w-xl" data-testid="migrate-preview-modal">
          <DialogHeader>
            <DialogTitle>PDF-Migration Vorschau</DialogTitle>
            <DialogDescription>
              Verschiebt base64-Anhänge aus der DB ins Filesystem (<code>{migratePreview?.target_dir}</code>).
              Frontend/Download funktioniert weiterhin (Backend liest <code>file_path</code>).
            </DialogDescription>
          </DialogHeader>
          {migratePreview && (
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-blue-50 border rounded">
                <p><b>Collection-Anhänge:</b> {migratePreview.collection_migrated} Stück ({migratePreview.collection_mb} MB)</p>
                <p><b>Array-Anhänge (in TN-Dokumenten):</b> {migratePreview.array_migrated} Stück ({migratePreview.array_mb} MB) bei {migratePreview.array_tn_count} TN</p>
              </div>
              <div className="p-3 bg-emerald-50 border-2 border-emerald-300 rounded">
                <p className="text-lg font-bold text-emerald-900">Wird freigegeben: {migratePreview.total_mb_freigegeben} MB</p>
              </div>
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                <b>Empfehlung:</b> Vorher Atlas-Backup ziehen (Atlas → Backups → Snapshot)
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMigratePreview(null)}>Abbrechen</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={confirmMigrate}
              disabled={cleanupRunning}
              data-testid="migrate-confirm-btn"
            >
              {cleanupRunning ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Jetzt migrieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AZAVMappe;
